import { useEffect, useRef } from "react";
import {
  useQuery,
  useMutation,
  useQueryClient,
  keepPreviousData,
  type QueryClient,
} from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { isAwaitingCarrier } from "@/lib/coiCarrierWait";
import { useLocation } from "wouter";
import type { Agency, Agent, InsertAgent, Policy, InsertAgency, InsertManualPolicy, InsertInsured, ActivityLog, PolicyDocument, InsuredDocument, CertificateHolder, InsertCertificateHolder, COIIssuance, Insured, Note, COIRequest, InboundEmail, InsuredCoiNotes, IntercomEvent, FeatureFlag, InsertFeatureFlag, UpdateFeatureFlag, InsuranceCompany, InsertInsuranceCompany, UpdateInsuranceCompany, InsuranceCompanyContact, InsertInsuranceCompanyContact, UpdateInsuranceCompanyContact, InsuranceCompanyCommissionRate, CoiNote, PolicyVehicle, PolicyDriver, PhysicalDamageVehicleSelection, AssetStatusChange, ForwardedRecoveryMeta, Commodity, InsuredCommodityLink } from "@shared/schema";
import type {
  AugmentedInsuredRow,
  PaginatedResponse,
  StatsTriple,
  PremiumMonthPoint,
  PolicySortField,
  InsuredSortField,
  IntercomSortField,
  IntercomStatusFilter,
  COIRequestSortField,
  COIRequestSource,
  COISentFilter,
  COINotSentReason,
  PolicyStatusFilter,
  PolicyStatusLiteral,
  StatusAlias,
  InsuredStatusFilter,
  SortDir,
} from "@shared/pagination";
import type { DateFilterQuery } from "@shared/list-filters";
import type { PolicySubstatus } from "@shared/policy-substatus";

import type {
  RecordUpdateRequestBody,
  PolicyRecordUpdateRow,
} from "@shared/recordUpdate";

import type { AcordExtraField, AcordFormId } from "@shared/acord-forms";
import {
  syncAttemptUnavailableMessage,
  type SyncAttemptUnavailableReason,
} from "@shared/sync-attempt";

import { ApiError, apiFetch, throwIfResNotOk } from "@/lib/queryClient";

export type ActivityLogWithUser = ActivityLog & {
  userName: string | null;
  userEmail: string | null;
};

const API_BASE = "/api";

// CSR-1445 — every mutation that writes an activity_logs row invalidates the
// global `["activity-logs"]` feed (Activity Log page). The Insured Detail
// timeline now reads its own server-scoped `["insured-activity-logs"]` query,
// so it MUST be invalidated alongside the global key or freshly-logged events
// (e.g. a manual sync) wouldn't appear right away. Since CSR-1638 the cache is
// fresh for 60s and refetches on window focus/reconnect, so the feed would
// eventually catch up — but explicit invalidation is what surfaces a just-logged
// event immediately, instead of only after the staleTime elapses or the next
// refocus. Invalidating the insured key from non-insured mutations is harmless:
// the query only refetches while mounted (insured detail page), so unrelated
// agency/agent mutations elsewhere just no-op against it.
function invalidateActivityLogs(client: QueryClient) {
  client.invalidateQueries({ queryKey: ["activity-logs"] });
  client.invalidateQueries({ queryKey: ["insured-activity-logs"] });
  // CSR-1637 — child-entity changes (Additional Insured / Loss Payee / Waiver)
  // also surface on the per-policy Activities tab, so refresh that feed too.
  client.invalidateQueries({ queryKey: ["policy-activity-logs"] });
}

// Agency API hooks
export function useAgencies() {
  return useQuery<Agency[]>({
    queryKey: ["agencies"],
    queryFn: async () => {
      const response = await apiFetch(`${API_BASE}/agencies`, { credentials: "include" });
      if (!response.ok) throw new Error("Failed to fetch agencies");
      return response.json();
    },
  });
}

export function useAgency(id: string) {
  return useQuery<Agency>({
    queryKey: ["agencies", id],
    queryFn: async () => {
      const response = await apiFetch(`${API_BASE}/agencies/${id}`, { credentials: "include" });
      if (!response.ok) throw new Error("Failed to fetch agency");
      return response.json();
    },
    enabled: !!id,
  });
}

export function useCreateAgency() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (agency: InsertAgency) => {
      const response = await apiFetch(`${API_BASE}/agencies`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(agency),
        credentials: "include",
      });
      if (!response.ok) throw new Error("Failed to create agency");
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["agencies"] });
    },
  });
}

export function useUpdateAgency() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<InsertAgency> }) => {
      const response = await apiFetch(`${API_BASE}/agencies/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
        credentials: "include",
      });
      if (!response.ok) throw new Error("Failed to update agency");
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["agencies"] });
    },
  });
}

export function useDeleteAgency() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const response = await apiFetch(`${API_BASE}/agencies/${id}`, {
        method: "DELETE",
        credentials: "include",
      });
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.message || "Failed to delete agency");
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["agencies"] });
      invalidateActivityLogs(queryClient);
    },
  });
}

// Agent API hooks
export function useAgents(agencyId?: string | null) {
  return useQuery<Agent[]>({
    queryKey: ["agents", agencyId],
    queryFn: async () => {
      const url = agencyId
        ? `${API_BASE}/agents?agencyId=${agencyId}`
        : `${API_BASE}/agents`;
      const response = await apiFetch(url, { credentials: "include" });
      if (!response.ok) throw new Error("Failed to fetch agents");
      return response.json();
    },
  });
}

export function useAgent(id: string) {
  return useQuery<Agent>({
    queryKey: ["agents", "detail", id],
    queryFn: async () => {
      const response = await apiFetch(`${API_BASE}/agents/${id}`, { credentials: "include" });
      if (!response.ok) throw new Error("Failed to fetch agent");
      return response.json();
    },
    enabled: !!id,
  });
}

export function useCreateAgent() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (agent: InsertAgent) => {
      const response = await apiFetch(`${API_BASE}/agents`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(agent),
        credentials: "include",
      });
      if (!response.ok) throw new Error("Failed to create agent");
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["agents"] });
      invalidateActivityLogs(queryClient);
    },
  });
}

export function useUpdateAgent() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<InsertAgent> }) => {
      const response = await apiFetch(`${API_BASE}/agents/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
        credentials: "include",
      });
      if (!response.ok) throw new Error("Failed to update agent");
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["agents"] });
      invalidateActivityLogs(queryClient);
    },
  });
}

export function useDeleteAgent() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const response = await apiFetch(`${API_BASE}/agents/${id}`, {
        method: "DELETE",
        credentials: "include",
      });
      if (!response.ok) throw new Error("Failed to delete agent");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["agents"] });
      queryClient.invalidateQueries({ queryKey: ["policies"] });
      invalidateActivityLogs(queryClient);
    },
  });
}

// Carrier API hooks
export interface CarrierInfo {
  name: string;
  parentCarrier: string | null;
}

export function useCarriers() {
  return useQuery<CarrierInfo[]>({
    queryKey: ["carriers"],
    queryFn: async () => {
      const response = await apiFetch(`${API_BASE}/carriers`, { credentials: "include" });
      if (!response.ok) throw new Error("Failed to fetch carriers");
      return response.json();
    },
  });
}

// Policy API hooks (read-only, synced from Data Center)
// CSR-674: paginated. The hook is parametric — callers pass a query
// options object. The query key includes every option so different
// (page, q, status, ...) combinations cache independently.
export interface PoliciesQueryOptions {
  agencyId?: string | null;
  page?: number;
  pageSize?: number;
  q?: string;
  bucket?: StatusAlias;
  status?: PolicyStatusFilter;
  advancedStatus?: PolicyStatusLiteral;
  substatus?: PolicySubstatus;
  carrier?: string;
  mga?: string;
  parentCarrier?: string;
  effectiveDate?: DateFilterQuery;
  expirationDate?: DateFilterQuery;
  cancellationDate?: DateFilterQuery;
  sort?: PolicySortField;
  sortDir?: SortDir;
  enabled?: boolean;
}

function appendDateFilter(
  params: URLSearchParams,
  name: string,
  value: DateFilterQuery | undefined,
) {
  if (value?.exact) params.set(name, value.exact);
  if (value?.from) params.set(`${name}From`, value.from);
  if (value?.to) params.set(`${name}To`, value.to);
}

function policyListParams(options: PoliciesQueryOptions): URLSearchParams {
  const params = new URLSearchParams();
  if (options.agencyId) params.set("agencyId", options.agencyId);
  if (options.page != null) params.set("page", String(options.page));
  if (options.pageSize != null) params.set("pageSize", String(options.pageSize));
  if (options.q) params.set("q", options.q);
  if (options.bucket) params.set("bucket", options.bucket);
  if (options.advancedStatus) params.set("status", options.advancedStatus);
  else if (options.status) params.set("status", options.status);
  if (options.substatus) params.set("substatus", options.substatus);
  if (options.carrier) params.set("carrier", options.carrier);
  if (options.mga) params.set("mga", options.mga);
  if (options.parentCarrier) params.set("parentCarrier", options.parentCarrier);
  appendDateFilter(params, "effectiveDate", options.effectiveDate);
  appendDateFilter(params, "expirationDate", options.expirationDate);
  appendDateFilter(params, "cancellationDate", options.cancellationDate);
  if (options.sort) params.set("sort", options.sort);
  if (options.sortDir) params.set("sortDir", options.sortDir);
  return params;
}

// CSR-748: list rows are augmented with the joined insured name so the
// `/policies` table can show the linked insured (FK) instead of the legacy
// free-text customerName. Falls back to null for orphan policies.
export type PolicyListRow = Policy & { insuredName: string | null };

export interface PoliciesResponse extends PaginatedResponse<PolicyListRow> {
  lastSyncedAt: string | null;
  syncStatus: string | null;
}

export function usePolicies(options: PoliciesQueryOptions = {}) {
  const { enabled } = options;
  return useQuery<PoliciesResponse>({
    queryKey: ["policies", options],
    queryFn: async () => {
      const params = policyListParams(options);
      const url = params.size > 0
        ? `${API_BASE}/policies?${params.toString()}`
        : `${API_BASE}/policies`;
      const response = await apiFetch(url, { credentials: "include" });
      if (!response.ok) throw new Error("Failed to fetch policies");
      return response.json();
    },
    placeholderData: keepPreviousData,
    enabled: enabled !== false,
  });
}

export type CsvExportResult =
  | { kind: "downloaded"; filename: string }
  | { kind: "empty" };

async function downloadCsv(
  response: Response,
  fallbackName: string,
): Promise<CsvExportResult> {
  if (response.status === 204) return { kind: "empty" };
  await throwIfResNotOk(response);
  const disposition = response.headers.get("content-disposition") ?? "";
  const filename = /filename="([^"]+)"/i.exec(disposition)?.[1] ?? fallbackName;
  const href = URL.createObjectURL(await response.blob());
  try {
    const link = document.createElement("a");
    link.href = href;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
  } finally {
    URL.revokeObjectURL(href);
  }
  return { kind: "downloaded", filename };
}

export async function exportPoliciesCsv(
  options: PoliciesQueryOptions,
): Promise<CsvExportResult> {
  const params = policyListParams({
    ...options,
    page: undefined,
    pageSize: undefined,
  });
  const response = await apiFetch(
    `${API_BASE}/policies/export${params.size ? `?${params.toString()}` : ""}`,
    { credentials: "include" },
  );
  return downloadCsv(response, "policies.csv");
}

// CSR-1343: per-carrier sync-health for the global "carrier unstable" banner.
export interface CarrierSyncHealth {
  carrier: string;
  jobType: string;
  show: boolean;
}

export function useCarrierSyncHealth() {
  return useQuery<CarrierSyncHealth[]>({
    queryKey: ["carrier-sync-health"],
    queryFn: async () => {
      const response = await apiFetch(`${API_BASE}/sync/carrier-health`, {
        credentials: "include",
      });
      if (!response.ok) throw new Error("Failed to fetch carrier sync health");
      return response.json();
    },
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
    staleTime: 30_000,
  });
}

// CSR-674: filter-blind (agencyId only). Powers the chip stats.
export function usePoliciesStats(agencyId?: string | null) {
  return useQuery<StatsTriple>({
    queryKey: ["policies-stats", agencyId],
    queryFn: async () => {
      const url = agencyId
        ? `${API_BASE}/policies/stats?agencyId=${agencyId}`
        : `${API_BASE}/policies/stats`;
      const response = await apiFetch(url, { credentials: "include" });
      if (!response.ok) throw new Error("Failed to fetch policies stats");
      return response.json();
    },
  });
}

// CSR-674: monthly aggregate for the dashboard premium-growth chart.
export function usePremiumByMonth(agencyId?: string | null) {
  return useQuery<PremiumMonthPoint[]>({
    queryKey: ["premium-by-month", agencyId],
    queryFn: async () => {
      const url = agencyId
        ? `${API_BASE}/policies/premium-by-month?agencyId=${agencyId}`
        : `${API_BASE}/policies/premium-by-month`;
      const response = await apiFetch(url, { credentials: "include" });
      if (!response.ok) throw new Error("Failed to fetch premium by month");
      return response.json();
    },
  });
}

// CSR-674: dedicated, capped (500) endpoint for one insured's policies.
// Replaces the bulk-list path that InsuredDetail*.tsx and Dashboard
// previously used.
export function useInsuredPolicies(
  insuredId: string | null | undefined,
  agencyId?: string | null,
) {
  return useQuery<{ data: Policy[] }>({
    queryKey: ["insured-policies", insuredId, agencyId],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (agencyId) params.set("agencyId", agencyId);
      const url = `${API_BASE}/insureds/${encodeURIComponent(insuredId!)}/policies${
        params.size > 0 ? `?${params.toString()}` : ""
      }`;
      const response = await apiFetch(url, { credentials: "include" });
      if (!response.ok) throw new Error("Failed to fetch insured policies");
      return response.json();
    },
    enabled: !!insuredId,
  });
}

export function usePolicy(id: string) {
  return useQuery<Policy>({
    queryKey: ["policies", "detail", id],
    queryFn: async () => {
      const response = await apiFetch(`${API_BASE}/policies/${id}`, { credentials: "include" });
      if (!response.ok) throw new Error("Failed to fetch policy");
      return response.json();
    },
    enabled: !!id,
  });
}

// CSR-674: paginated insureds list. Returns the augmented row shape
// (every Insured field + 6 derived fields). The legacy zero-arg call
// signature is gone — callers pass an options object.
export interface InsuredsQueryOptions {
  agencyId?: string | null;
  page?: number;
  pageSize?: number;
  q?: string;
  nameQ?: string;
  bucket?: StatusAlias;
  status?: InsuredStatusFilter;
  advancedStatus?: "Active" | "Inactive";
  salesAgent?: string;
  retentionAgent?: string;
  state?: string;
  businessType?: "New Business" | "Renewal";
  sort?: InsuredSortField;
  sortDir?: SortDir;
  includeUnmatched?: boolean;
  enabled?: boolean;
}

function insuredListParams(options: InsuredsQueryOptions): URLSearchParams {
  const params = new URLSearchParams();
  if (options.agencyId) params.set("agencyId", options.agencyId);
  if (options.page != null) params.set("page", String(options.page));
  if (options.pageSize != null) params.set("pageSize", String(options.pageSize));
  if (options.q) params.set("q", options.q);
  if (options.nameQ) params.set("nameQ", options.nameQ);
  if (options.bucket) params.set("bucket", options.bucket);
  if (options.advancedStatus) params.set("status", options.advancedStatus);
  else if (options.status) params.set("status", options.status);
  if (options.salesAgent) params.set("salesAgent", options.salesAgent);
  if (options.retentionAgent) params.set("retentionAgent", options.retentionAgent);
  if (options.state) params.set("state", options.state);
  if (options.businessType) params.set("businessType", options.businessType);
  if (options.sort) params.set("sort", options.sort);
  if (options.sortDir) params.set("sortDir", options.sortDir);
  if (options.includeUnmatched != null) {
    params.set("includeUnmatched", String(options.includeUnmatched));
  }
  return params;
}

export type InsuredsResponse = PaginatedResponse<AugmentedInsuredRow>;

export function useInsureds(options: InsuredsQueryOptions = {}) {
  const { enabled } = options;
  return useQuery<InsuredsResponse>({
    queryKey: ["insureds", options],
    queryFn: async () => {
      const params = insuredListParams(options);
      const url = params.size > 0
        ? `${API_BASE}/insureds?${params.toString()}`
        : `${API_BASE}/insureds`;
      const response = await apiFetch(url, { credentials: "include" });
      // CSR-1989 — throws an ApiError carrying the status, so the retry policy
      // can distinguish a transient 5xx from a 401 and Sentry records which.
      await throwIfResNotOk(response);
      return response.json();
    },
    placeholderData: keepPreviousData,
    enabled: enabled !== false,
  });
}

export async function exportInsuredsCsv(
  options: InsuredsQueryOptions,
): Promise<CsvExportResult> {
  const params = insuredListParams({
    ...options,
    page: undefined,
    pageSize: undefined,
  });
  const response = await apiFetch(
    `${API_BASE}/insureds/export${params.size ? `?${params.toString()}` : ""}`,
    { credentials: "include" },
  );
  return downloadCsv(response, "insureds.csv");
}

// CSR-674: insureds chip stats (filter-blind, agencyId only).
export function useInsuredsStats(agencyId?: string | null) {
  return useQuery<StatsTriple>({
    queryKey: ["insureds-stats", agencyId],
    queryFn: async () => {
      const url = agencyId
        ? `${API_BASE}/insureds/stats?agencyId=${agencyId}`
        : `${API_BASE}/insureds/stats`;
      const response = await apiFetch(url, { credentials: "include" });
      if (!response.ok) throw new Error("Failed to fetch insureds stats");
      return response.json();
    },
  });
}

// CSR-1157 — insureds with zero linked policies, paginated. Powers the
// System Settings "Insureds without policies" tab. The row projection is
// thin on purpose (id/name/sources/createdAt) — anything richer should
// route through useInsureds() instead.
export type InsuredWithoutPoliciesRow = {
  id: string;
  name: string | null;
  sources: string | null;
  createdAt: string | null;
};

export type InsuredsWithoutPoliciesPayload = {
  totalCount: number;
  page: number;
  limit: number;
  items: InsuredWithoutPoliciesRow[];
};

export function useInsuredsWithoutPolicies(filters: { page: number; limit: number }) {
  return useQuery<InsuredsWithoutPoliciesPayload>({
    queryKey: ["insureds", "without-policies", filters.page, filters.limit],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.set("page", String(filters.page));
      params.set("limit", String(filters.limit));
      const response = await apiFetch(
        `${API_BASE}/insureds/without-policies?${params.toString()}`,
        { credentials: "include" },
      );
      if (!response.ok) throw new Error("Failed to fetch insureds without policies");
      return response.json();
    },
    placeholderData: keepPreviousData,
  });
}

// CSR-1993 — System Settings "Stale Sync": policies the carrier sweep has
// stopped refreshing, read through from DC (CSR-1992).
export type StaleSyncAttempt = {
  /** DC's raw vocabulary — narrow with `asDcSyncAttemptStatus` before labeling. */
  status: string | null;
  errorMessage: string | null;
  startedAt: string | null;
  finishedAt: string | null;
};

export type StaleSyncPolicyRow = {
  policyId: string | null;
  policyNumber: string | null;
  carrier: string | null;
  parentCarrier: string | null;
  customerName: string | null;
  status: string | null;
  insuredId: string | null;
  insuredName: string | null;
  /** Null together with `hoursSinceLastSync` when the policy was never synced. */
  lastSync: string | null;
  hoursSinceLastSync: number | null;
  /** ⚠️ Null means EITHER "never attempted" OR "an attempt exists but DC could
   *  not attribute it to this term". Never render it as "never synced" — that
   *  is what a null `lastSync` means. */
  latestSyncAttempt: StaleSyncAttempt | null;
  /** CSR-2134 — when the DC row was created, so a recent mistake stands out
   *  from old debris. */
  createdAt: string | null;
  /** CSR-2134 — consecutive carrier rejections since the last success. `0` is a
   *  fact ("the last attempt succeeded"), so render it, never treat it as
   *  missing. Null means DC did not report a streak. */
  notFoundStreak: number | null;
  /** CSR-2134 — ⚠️ three-way, and the cases are different claims:
   *  a string → the carrier last returned data then; `null` → the carrier has
   *  **NEVER** returned data for this number; `undefined` → DC did not report
   *  the field (a deploy predating CSR-2133). Rendering `undefined` as "Never"
   *  would accuse the whole book because DC is one release behind. */
  lastSuccessAt?: string | null;
  /** CSR-2327 — when the policy was flagged AOR'd away, else null. */
  aorIdentifiedAt?: string | null;
};

export type StaleSyncPoliciesPayload = {
  data: StaleSyncPolicyRow[];
  total: number;
  page: number;
  pageSize: number;
};

/** CSR-2094 — column filters run in the browser, so they need the whole set in
 *  memory. DC caps a page at 100, so anything larger takes several requests.
 *  This ceiling is what stops the widened "all statuses" view (~3280 rows in
 *  production) from firing 33 requests: past it the tab disables filtering
 *  instead. 500 covers the in-force set in both production (~92) and staging
 *  (~268) with headroom. */
export const STALE_SYNC_MAX_CLIENT_ROWS = 500;

export function useStaleSyncPolicies(filters: {
  /** CSR-2134 — `"all"` asks for the whole carrier work-list. A policy the
   *  carrier rejects on every sweep is re-checked constantly, so no numeric
   *  threshold can ever reach it. */
  staleForHours: number | "all";
  /** Empty string = both books (what the cleared selector sends). */
  carrier: string;
  /** CSR-2069 — narrow to terms whose recorded status implies coverage.
   *  DC does the filtering (CSR-2090); it has to, because the endpoint
   *  paginates server-side. */
  inForceStatusOnly: boolean;
  /** CSR-2134 — the attempt-history narrowings. Server-side for the same
   *  reason: they select the population, and the population is paginated. */
  neverSucceeded: boolean;
  minNotFoundStreak: number | null;
  createdWithinDays: number | null;
  page: number;
  pageSize: number;
}) {
  return useQuery<StaleSyncPoliciesPayload>({
    queryKey: [
      "policies",
      "stale-sync",
      filters.staleForHours,
      filters.carrier,
      filters.inForceStatusOnly,
      filters.neverSucceeded,
      filters.minNotFoundStreak,
      filters.createdWithinDays,
      filters.page,
      filters.pageSize,
    ],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.set("staleForHours", String(filters.staleForHours));
      if (filters.carrier) params.set("carrier", filters.carrier);
      if (filters.inForceStatusOnly) params.set("inForceStatusOnly", "true");
      if (filters.neverSucceeded) params.set("neverSucceeded", "true");
      if (filters.minNotFoundStreak != null) {
        params.set("minNotFoundStreak", String(filters.minNotFoundStreak));
      }
      if (filters.createdWithinDays != null) {
        params.set("createdWithinDays", String(filters.createdWithinDays));
      }
      params.set("page", String(filters.page));
      params.set("pageSize", String(filters.pageSize));
      const fetchPage = async (page: number) => {
        params.set("page", String(page));
        const response = await apiFetch(
          `${API_BASE}/policies/stale-sync?${params.toString()}`,
          { credentials: "include" },
        );
        if (!response.ok) throw new Error("Failed to fetch stale sync policies");
        return (await response.json()) as StaleSyncPoliciesPayload;
      };

      const first = await fetchPage(filters.page);
      // Only the caller asking for the whole set (page 1) can be completed;
      // a caller paging through explicitly gets exactly the page it asked for.
      const wantsWholeSet = filters.page === 1;
      const fits = first.total <= STALE_SYNC_MAX_CLIENT_ROWS;
      if (!wantsWholeSet || !fits || first.total <= filters.pageSize) {
        return first;
      }

      const pages = Math.ceil(first.total / filters.pageSize);
      const rest = await Promise.all(
        Array.from({ length: pages - 1 }, (_, i) => fetchPage(i + 2)),
      );
      return {
        ...first,
        data: [...first.data, ...rest.flatMap((p) => p.data)],
      };
    },
    placeholderData: keepPreviousData,
  });
}

export function useGlobalSearch(query: string) {
  // CSR-1489 — search policies carry a resolved `insuredName` (composed from the
  // linked insured) alongside the raw scraped `customerName`.
  return useQuery<{ insureds: Insured[]; policies: (Policy & { insuredName: string | null })[] }>({
    queryKey: ["global-search", query],
    queryFn: async () => {
      const response = await apiFetch(
        `${API_BASE}/search?q=${encodeURIComponent(query)}`,
        { credentials: "include" }
      );
      if (!response.ok) throw new Error("Failed to search");
      return response.json();
    },
    enabled: query.length >= 2,
    // CSR-1638 — search must always reflect current backend data. Without this,
    // a cached "no matches" result for a query string (e.g. a policy number not
    // yet synced) is served forever until a hard reload, even after the record
    // is created. staleTime: 0 makes every re-run of a search hit the network.
    staleTime: 0,
  });
}

export function useInsuredByName(name: string) {
  return useQuery<Insured | null>({
    queryKey: ["insureds", "by-name", name],
    queryFn: async () => {
      const response = await apiFetch(
        `${API_BASE}/insureds/by-name/${encodeURIComponent(name)}`,
        { credentials: "include" }
      );
      if (response.status === 404) return null;
      if (!response.ok) throw new Error("Failed to fetch insured");
      return response.json();
    },
    enabled: !!name,
  });
}

export function useInsuredByDotNumber(dotNumber: string | null) {
  return useQuery<Insured | null>({
    queryKey: ["insureds", "by-dot", dotNumber],
    queryFn: async () => {
      const response = await apiFetch(
        `${API_BASE}/insureds/by-dot/${encodeURIComponent(dotNumber!)}`,
        { credentials: "include" }
      );
      if (response.status === 404) return null;
      if (!response.ok) throw new Error("Failed to fetch insured");
      return response.json();
    },
    enabled: !!dotNumber,
  });
}

export function useInsured(id: string | null) {
  return useQuery<Insured | null>({
    queryKey: ["insureds", "detail", id],
    queryFn: async () => {
      const response = await apiFetch(`${API_BASE}/insureds/${id}`, { credentials: "include" });
      if (response.status === 404) return null;
      if (!response.ok) throw new Error("Failed to fetch insured");
      return response.json();
    },
    enabled: !!id,
  });
}

export function useUpdateInsured() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data, note }: { id: string; data: Partial<InsertInsured>; note?: string }) => {
      const response = await apiFetch(`${API_BASE}/insureds/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...data, note }),
        credentials: "include",
      });
      if (!response.ok) throw new Error("Failed to update insured");
      return response.json();
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["insureds"] });
      queryClient.invalidateQueries({ queryKey: ["insureds", "detail", variables.id] });
      invalidateActivityLogs(queryClient);
      queryClient.invalidateQueries({ queryKey: ["insured-change-history", variables.id] });
    },
  });
}

// CSR-1970 — delete an insured. The DC soft-deletes the record AND its live
// policies; nothing here is recoverable from the UI, which is why the caller
// must go through DeleteInsuredDialog rather than firing this directly.
//
// The insured detail query is REMOVED from the cache rather than invalidated:
// invalidating would refetch `/insureds/:id`, which now 404s, leaving the page
// that is mid-navigation showing an error flash on the way out.
export function useDeleteInsured() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const response = await apiFetch(`${API_BASE}/insureds/${id}`, {
        method: "DELETE",
        credentials: "include",
      });
      if (!response.ok) {
        const body = await response.json().catch(() => null);
        throw new Error(body?.error ?? "Failed to delete insured");
      }
    },
    onSuccess: (_data, id) => {
      queryClient.removeQueries({ queryKey: ["insureds", "detail", id] });
      queryClient.removeQueries({ queryKey: ["insured-policies", id] });
      queryClient.invalidateQueries({ queryKey: ["insureds"] });
      // The insured and its policies both vanish from search and the policy
      // lists, so those caches are stale too.
      queryClient.invalidateQueries({ queryKey: ["policies"] });
      // The global search query is keyed ["global-search", query] — an invalidation
      // of ["search"] matches nothing and would leave the deleted insured on
      // screen until an unrelated refetch.
      queryClient.invalidateQueries({ queryKey: ["global-search"] });
      // Insured/policy counts and premiums both move when a delete cascades.
      queryClient.invalidateQueries({ queryKey: ["insureds-stats"] });
      queryClient.invalidateQueries({ queryKey: ["policies-stats"] });
      invalidateActivityLogs(queryClient);
    },
  });
}

// CSR-1823 — `skipContactDedup` is the DC force-create opt-out (CSR-1822): when a
// deliberate "Create new insured" is chosen on a contact-only (email/phone)
// match, it tells DC to insert a distinct row instead of silently merging onto
// the insured that shares the contact. It is not a DB column, so it rides
// alongside the insured fields and is read straight off the POST body server-side.
export type CreateInsuredInput = Partial<InsertInsured> & { skipContactDedup?: boolean };

export function useCreateInsured() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: CreateInsuredInput) => {
      const response = await apiFetch(`${API_BASE}/insureds`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
        credentials: "include",
      });
      if (!response.ok) throw new Error("Failed to create insured");
      const result = await response.json();
      return result as Insured;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["insureds"] });
      invalidateActivityLogs(queryClient);
    },
  });
}

export function useGetInsuredByDot(dotNumber: string | null) {
  return useQuery({
    queryKey: ['insureds', 'by-dot', dotNumber],
    queryFn: async () => {
      if (!dotNumber) return null;
      try {
        const res = await apiFetch(`${API_BASE}/insureds/by-dot/${encodeURIComponent(dotNumber)}`, { credentials: 'include' });
        if (!res.ok) return null;
        return await res.json();
      } catch {
        return null;
      }
    },
    enabled: false,
  });
}

export function useGetInsuredByName(name: string | null) {
  return useQuery({
    queryKey: ['insureds', 'by-name', name],
    queryFn: async () => {
      if (!name) return null;
      try {
        const res = await apiFetch(`${API_BASE}/insureds/by-name/${encodeURIComponent(name)}`, { credentials: 'include' });
        if (!res.ok) return null;
        return await res.json();
      } catch {
        return null;
      }
    },
    enabled: false,
  });
}

// CSR-1823 — pre-save dedup lookup by email, on demand (enabled:false + refetch),
// mirroring by-dot/by-name. Backs the Add-Insured alert's parity with DC's merge
// keys: DC merges a manual create onto an existing insured that shares an email,
// so the modal must be able to detect that match before saving.
export function useGetInsuredByEmail(email: string | null) {
  return useQuery({
    queryKey: ['insureds', 'by-email', email],
    queryFn: async () => {
      if (!email) return null;
      try {
        const res = await apiFetch(`${API_BASE}/insureds/by-email/${encodeURIComponent(email)}`, { credentials: 'include' });
        if (!res.ok) return null;
        return await res.json();
      } catch {
        return null;
      }
    },
    enabled: false,
  });
}

// CSR-1831 — pre-save dedup lookup by phone, on demand (enabled:false + refetch),
// mirroring by-email. Completes the Add-Insured alert's parity with DC's merge
// keys (DOT → email → phone): DC merges a manual create onto an existing insured
// that shares a phone, so the modal must be able to detect that match before saving.
export function useGetInsuredByPhone(phone: string | null) {
  return useQuery({
    queryKey: ['insureds', 'by-phone', phone],
    queryFn: async () => {
      if (!phone) return null;
      try {
        const res = await apiFetch(`${API_BASE}/insureds/by-phone/${encodeURIComponent(phone)}`, { credentials: 'include' });
        if (!res.ok) return null;
        return await res.json();
      } catch {
        return null;
      }
    },
    enabled: false,
  });
}

export interface InsuredContact {
  name: string;
  email?: string | null;
  phone?: string | null;
  isPrimary?: boolean;
  createdAt?: string;
}

export function useInsuredContacts(insuredId: string | undefined) {
  return useQuery({
    queryKey: ["insured-contacts", insuredId],
    queryFn: async () => {
      if (!insuredId) return [] as InsuredContact[];
      const response = await apiFetch(`${API_BASE}/insureds/${insuredId}/contacts`, { credentials: "include" });
      if (!response.ok) throw new Error("Failed to fetch contacts");
      const result = await response.json();
      return result as InsuredContact[];
    },
    enabled: !!insuredId,
  });
}

export function useReplaceInsuredContacts(insuredId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (contacts: InsuredContact[]) => {
      const response = await apiFetch(`${API_BASE}/insureds/${insuredId}/contacts`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ contacts }),
        credentials: "include",
      });
      if (!response.ok) throw new Error("Failed to replace contacts");
      const result = await response.json();
      return result as InsuredContact[];
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["insured-contacts", insuredId] });
      queryClient.invalidateQueries({ queryKey: ["insureds", "detail", insuredId] });
      invalidateActivityLogs(queryClient);
    },
  });
}

export interface InsuredChangeHistoryEntry {
  id: string;
  insuredId: string;
  field: string;
  oldValue: string | null;
  newValue: string | null;
  source: string;
  changedBy: string | null;
  changedByName: string | null;
  note: string | null;
  changedAt: string;
}

export function useInsuredChangeHistory(insuredId: string | null) {
  return useQuery<InsuredChangeHistoryEntry[]>({
    queryKey: ["insured-change-history", insuredId],
    queryFn: async () => {
      const response = await apiFetch(`${API_BASE}/insureds/${insuredId}/change-history`, { credentials: "include" });
      if (!response.ok) throw new Error("Failed to fetch change history");
      return response.json();
    },
    enabled: !!insuredId,
  });
}

// CSR-1124 — Flint is the source of truth for the producer roster. AMS
// proxies the upstream call (see server/services/flintProducers.ts) so the
// browser doesn't depend on Flint CORS and we can add auth server-side
// later without touching the client.
export interface FlintProducer {
  fullName: string;
  email: string;
  key: string;
}

export function useFlintProducers() {
  return useQuery<FlintProducer[]>({
    queryKey: ["producers"],
    queryFn: async () => {
      const response = await apiFetch(`${API_BASE}/producers`, {
        credentials: "include",
      });
      if (!response.ok) throw new Error("Failed to fetch producers");
      return response.json();
    },
    staleTime: 5 * 60 * 1000,
  });
}

// CSR-1305 / CSR-2167 — Sales Agent options for the insured Account Team
// dropdown: Flint's full producer roster including soft-deleted producers
// (terminated or moved to another role), so historical agents stay
// selectable while the field is selection-only (see GET /api/sales-agents).
export function useSalesAgents() {
  return useQuery<string[]>({
    queryKey: ["sales-agents"],
    queryFn: async () => {
      const response = await apiFetch(`${API_BASE}/sales-agents`, {
        credentials: "include",
      });
      if (!response.ok) throw new Error("Failed to fetch sales agents");
      return response.json();
    },
    staleTime: 5 * 60 * 1000,
  });
}

export function useCreatePolicy() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (policy: InsertManualPolicy) => {
      const response = await apiFetch(`${API_BASE}/policies`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(policy),
        credentials: "include",
      });
      if (!response.ok) throw new Error("Failed to create policy");
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["policies"] });
      // CSR-1142: the insured Policies tab reads via ["insured-policies", ...],
      // which is NOT a prefix of ["policies"]; without this the new policy only
      // appears after a full page reload.
      queryClient.invalidateQueries({ queryKey: ["insured-policies"] });
      queryClient.invalidateQueries({ queryKey: ["carriers"] });
      invalidateActivityLogs(queryClient);
    },
  });
}

export function useUpdatePolicy() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data, note }: { id: string; data: Partial<InsertManualPolicy>; note?: string }) => {
      const response = await apiFetch(`${API_BASE}/policies/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...data, note }),
        credentials: "include",
      });
      if (!response.ok) {
        let errorMessage = "Failed to update policy";
        try {
          const data = await response.json();
          if (data?.error) errorMessage = data.error;
        } catch {
          // body wasn't JSON — keep the default
        }
        throw new Error(errorMessage);
      }
      return response.json();
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["policies"] });
      queryClient.invalidateQueries({ queryKey: ["policies", "detail", variables.id] });
      // CSR-1142: keep the insured Policies tab in sync (see useCreatePolicy).
      queryClient.invalidateQueries({ queryKey: ["insured-policies"] });
      invalidateActivityLogs(queryClient);
      queryClient.invalidateQueries({ queryKey: ["policy-change-history", variables.id] });
    },
  });
}

// CSR-1971 / CSR-1967 — record an endorsement / cancellation / reinstatement.
// POSTs the client-computed body to the AMS thin proxy, which enforces the
// tenant guard and forwards to DC's validating writer. A Record Update changes
// the policy premium/status like an edit, so it reuses useUpdatePolicy's
// invalidation set — the policy header, both policy lists, the activity logs,
// and the change-history feed all refresh on success.
export function useRecordPolicyUpdate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, body }: { id: string; body: RecordUpdateRequestBody }) => {
      const response = await apiFetch(`${API_BASE}/policies/${id}/record-updates`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
        credentials: "include",
      });
      if (!response.ok) {
        let errorMessage = "Failed to record the update";
        try {
          const data = await response.json();
          if (data?.error) errorMessage = data.error;
        } catch {
          // body wasn't JSON — keep the default
        }
        throw new Error(errorMessage);
      }
      return response.json();
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["policies"] });
      queryClient.invalidateQueries({ queryKey: ["policies", "detail", variables.id] });
      queryClient.invalidateQueries({ queryKey: ["insured-policies"] });
      invalidateActivityLogs(queryClient);
      queryClient.invalidateQueries({ queryKey: ["policy-change-history", variables.id] });
      queryClient.invalidateQueries({ queryKey: ["policy-record-updates", variables.id] });
    },
  });
}

// CSR-2326 — "Customer AOR'd away" (flag) and "Access restored" (restore).
// Both answer with the read-shaped policy, so the caller can update in place;
// every view that shows the AOR marker is refreshed.
function useWritePolicyAor(method: "POST" | "DELETE", fallbackError: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      carrierAgencyCode,
    }: {
      id: string;
      carrierAgencyCode?: string | null;
    }): Promise<Policy> => {
      const response = await apiFetch(`${API_BASE}/policies/${id}/aor`, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(
          method === "POST" && carrierAgencyCode ? { carrierAgencyCode } : {},
        ),
        credentials: "include",
      });
      if (!response.ok) {
        let errorMessage = fallbackError;
        try {
          const data = await response.json();
          if (data?.error) errorMessage = data.error;
        } catch {
          // body wasn't JSON — keep the default
        }
        throw new Error(errorMessage);
      }
      return response.json();
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["policies"] });
      queryClient.invalidateQueries({ queryKey: ["policies", "detail", variables.id] });
      queryClient.invalidateQueries({ queryKey: ["insured-policies"] });
      invalidateActivityLogs(queryClient);
      // DC writes the flag to every live term of the policy number, so every
      // term's history moves — not only the one the agent acted on.
      queryClient.invalidateQueries({ queryKey: ["policy-change-history"] });
    },
  });
}

export function useFlagPolicyAor() {
  return useWritePolicyAor("POST", "Failed to flag the policy as AOR'd away");
}

export function useRestorePolicyAor() {
  return useWritePolicyAor("DELETE", "Failed to restore access to the policy");
}

export function useUpdatePolicyVehicles() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      vehicles,
      assetStatusChange,
    }: {
      id: string;
      vehicles: PolicyVehicle[];
      assetStatusChange?: AssetStatusChange;
    }) => {
      const response = await apiFetch(`${API_BASE}/policies/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ vehicles, assetStatusChange }),
        credentials: "include",
      });
      if (!response.ok) throw new Error("Failed to update vehicles");
      return response.json();
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["policies"] });
      queryClient.invalidateQueries({ queryKey: ["policies", "detail", variables.id] });
      // The Assets tab reads policies via ["insured-policies", ...]; that key is
      // NOT a prefix of ["policies"], so it must be invalidated explicitly or the
      // edited vehicle won't reflect until a full page reload.
      queryClient.invalidateQueries({ queryKey: ["insured-policies"] });
      invalidateActivityLogs(queryClient);
      queryClient.invalidateQueries({ queryKey: ["policy-change-history", variables.id] });
    },
  });
}

export function useUpdatePolicyTrailers() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      trailers,
      assetStatusChange,
    }: {
      id: string;
      trailers: PolicyVehicle[];
      assetStatusChange?: AssetStatusChange;
    }) => {
      const response = await apiFetch(`${API_BASE}/policies/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ trailers, assetStatusChange }),
        credentials: "include",
      });
      if (!response.ok) throw new Error("Failed to update trailers");
      return response.json();
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["policies"] });
      queryClient.invalidateQueries({ queryKey: ["policies", "detail", variables.id] });
      queryClient.invalidateQueries({ queryKey: ["insured-policies"] });
      invalidateActivityLogs(queryClient);
      queryClient.invalidateQueries({ queryKey: ["policy-change-history", variables.id] });
    },
  });
}

export function useUpdatePolicyDrivers() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      drivers,
      assetStatusChange,
    }: {
      id: string;
      drivers: PolicyDriver[];
      assetStatusChange?: AssetStatusChange;
    }) => {
      const response = await apiFetch(`${API_BASE}/policies/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ drivers, assetStatusChange }),
        credentials: "include",
      });
      if (!response.ok) throw new Error("Failed to update drivers");
      return response.json();
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["policies"] });
      queryClient.invalidateQueries({ queryKey: ["policies", "detail", variables.id] });
      queryClient.invalidateQueries({ queryKey: ["insured-policies"] });
      invalidateActivityLogs(queryClient);
      queryClient.invalidateQueries({ queryKey: ["policy-change-history", variables.id] });
    },
  });
}

export function useDeletePolicy() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const response = await apiFetch(`${API_BASE}/policies/${id}`, {
        method: "DELETE",
        credentials: "include",
      });
      if (!response.ok) throw new Error("Failed to delete policy");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["policies"] });
      // CSR-1142: keep the insured Policies tab in sync (see useCreatePolicy).
      queryClient.invalidateQueries({ queryKey: ["insured-policies"] });
      invalidateActivityLogs(queryClient);
    },
  });
}

// CSR-1144: the mutation can carry an optional AMS-only Physical Damage vehicle
// selection alongside the coverages. Callers that only edit coverages pass just
// `{ coverages }`; the PD Add/Edit flow also passes `physicalDamageVehicles`.
export interface ReplaceCoveragesInput {
  coverages: Record<string, unknown>;
  physicalDamageVehicles?: PhysicalDamageVehicleSelection | null;
}

export function useReplaceCoverages(policyId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: ReplaceCoveragesInput) => {
      const response = await apiFetch(`${API_BASE}/policies/${policyId}/coverages`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(input),
        credentials: "include",
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({ error: response.statusText }));
        throw new Error(err.error || "Failed to replace coverages");
      }
      return response.json() as Promise<Policy>;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["policies"] });
      queryClient.invalidateQueries({ queryKey: ["policies", "detail", policyId] });
      // CSR-1144: the PD card lives on the Insured detail page, which reads
      // policies via the ["insured-policies"] key — invalidate it too so the
      // curated selection shows without a manual reload.
      queryClient.invalidateQueries({ queryKey: ["insured-policies"] });
      invalidateActivityLogs(queryClient);
    },
  });
}

// CSR-1090: the browser fires the sync request and returns immediately.
// The server enqueues a pg-boss job that drives the per-policy poll.
// The chip/spinner is driven by `usePolicySyncStatus` below, which
// polls a cheap status endpoint via TanStack's `refetchInterval`.
export function useSyncSinglePolicy(policyId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const res = await apiFetch(`${API_BASE}/policies/${policyId}/sync`, {
        method: "POST",
        credentials: "include",
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        if (res.status === 503) {
          throw new Error("Carrier sync is paused on Data Center. Try again once it's running.");
        }
        throw new Error(body.error || "Failed to sync policy");
      }
      return res.json() as Promise<{ status: "queued"; policyId: string; fake: boolean }>;
    },
    onSuccess: () => {
      // Status query starts polling immediately because
      // `hasActivePolicySyncJob` is now true. We do NOT invalidate
      // detail/list queries here — wait until the poll detects the
      // transition to "not syncing" so we don't fetch stale data.
      queryClient.invalidateQueries({ queryKey: ["policy-sync-status", policyId] });
    },
  });
}

// CSR-1090: poll the per-policy "syncing now?" status. Drives the inline
// chip on PolicyDetail. Halts once the worker finishes.
//
// CSR-1090 cycle 2: also surfaces the most recent terminal outcome
// (`lastSyncOutcome` / `lastSyncError`) and fires a destructive toast on
// the syncing → done transition when the worker run failed. Restores the
// per-policy DC error surface that CSR-1076 originally added — see the
// review report for the regression details.
// CSR-1703: `lastSyncOutcome` widens to include `"not_found"` — a distinct
// terminal outcome for "the carrier scrape ran but could not locate this
// policy" (e.g. GEICO). Existing `=== "failed"` checks are unaffected; this
// is a new value they never saw before.

/**
 * The terminal outcomes a single-policy carrier sync can report.
 *
 * CSR-1977 adds `"no_change"`: DC (CSR-1976) reached the carrier and finished
 * cleanly, but nothing tracked actually differed. It is a SUCCESS — never
 * group it with `"failed"`/`"not_found"` — but it must stay distinguishable
 * from `"succeeded"`, because DC bumps its `last_sync` timestamp either way
 * and the header would otherwise claim data was updated when it wasn't.
 *
 * CSR-2174 adds `"unattributable"`: the scrape ran, but two live terms share
 * this policy number so DC will not say which one the result belongs to. Also
 * a SUCCESS — it is precisely the case that used to masquerade as a 7½-minute
 * timeout failure on ~45% of the GEICO book, so it must never be grouped with
 * `"failed"`/`"not_found"` either.
 */
export type PolicySyncOutcome =
  | "succeeded"
  | "no_change"
  | "failed"
  | "not_found"
  | "unattributable";

export interface PolicySyncStatusResponse {
  syncing: boolean;
  lastSyncedAt: string | null;
  lastSyncOutcome: PolicySyncOutcome | null;
  lastSyncError: string | null;
  lastSyncUnattributableReason: SyncAttemptUnavailableReason | null;
  lastSyncOutcomeAt: string | null;
}

export function usePolicySyncStatus(policyId: string, enabled = true) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  // Track the previous syncing state so we can fire one invalidate when
  // the boolean transitions from true → false.
  const prevSyncingRef = useRef<boolean | null>(null);
  // Record the "starting" outcome timestamp the first time we observe
  // syncing=true. We only want to surface a *new* failure (recorded
  // after the poll started) — a stale failure from a previous attempt
  // would otherwise toast on every page load that happens to land
  // mid-sync.
  const outcomeAtAtSyncStartRef = useRef<string | null>(null);

  const query = useQuery<PolicySyncStatusResponse>({
    queryKey: ["policy-sync-status", policyId],
    queryFn: async () => {
      const res = await apiFetch(`${API_BASE}/policies/${policyId}/sync-status`, {
        credentials: "include",
        cache: "no-store",
      });
      if (!res.ok) throw new Error("Failed to read sync status");
      return res.json();
    },
    enabled: enabled && !!policyId,
    // Mirrors the CSR-758 pattern: poll every 5s while syncing, halt
    // once the server reports the worker has finished.
    refetchInterval: (q) => (q.state.data?.syncing ? 5000 : false),
    // Keep the chip from briefly disappearing on tab refocus while we
    // already know the value.
    staleTime: 2_000,
  });

  useEffect(() => {
    if (query.data === undefined) return;
    const wasSyncing = prevSyncingRef.current;
    const isSyncing = query.data.syncing;
    prevSyncingRef.current = isSyncing;
    // Stamp the baseline outcome timestamp on the first observation
    // that the worker is in flight, so the transition check below can
    // tell a fresh failure from a stale one.
    if (wasSyncing !== true && isSyncing === true) {
      outcomeAtAtSyncStartRef.current = query.data.lastSyncOutcomeAt;
    }
    if (wasSyncing === true && isSyncing === false) {
      queryClient.invalidateQueries({ queryKey: ["policies", "detail", policyId] });
      queryClient.invalidateQueries({ queryKey: ["policies"] });
      queryClient.invalidateQueries({ queryKey: ["policy-change-history", policyId] });
      invalidateActivityLogs(queryClient);
      // CSR-1090 cycle 2: surface DC errors that the worker recorded on
      // this poll cycle. The chip just disappears, so a destructive
      // toast is the minimal way to keep the operator-facing failure
      // signal that CSR-1076 introduced.
      const isFreshOutcome =
        query.data.lastSyncOutcomeAt !== outcomeAtAtSyncStartRef.current;
      if (query.data.lastSyncOutcome === "failed" && isFreshOutcome) {
        toast({
          title: "Policy sync failed",
          description:
            query.data.lastSyncError ||
            "The carrier sync did not complete. Try again or check the activity log.",
          variant: "destructive",
        });
      }
      // CSR-2174: the poll ended in seconds and the refresh did NOT fail — DC
      // simply cannot tell which of two live terms sharing this policy number a
      // result belongs to. Two deliberate choices here:
      //  - the default (non-destructive) variant, because dressing an honest
      //    "we can't confirm which term" as an error is the same false alarm the
      //    ticket removes, just faster;
      //  - a title that claims no outcome. The carrier scrape AMS triggered runs
      //    asynchronously and is almost certainly still going at this point, so
      //    anything like "Refresh finished" would assert, in the one string the
      //    CSR reads first, exactly what the description below says we cannot
      //    know.
      if (query.data.lastSyncOutcome === "unattributable" && isFreshOutcome) {
        toast({
          title: "Carrier update not confirmed for this term",
          description: syncAttemptUnavailableMessage(
            query.data.lastSyncUnattributableReason,
          ),
        });
      }
      outcomeAtAtSyncStartRef.current = null;
    }
  }, [query.data, policyId, queryClient, toast]);

  return query;
}

// CSR-1090: the insured sync request returns immediately; the spinner +
// chip status is driven by `useInsuredPoliciesSyncStatus` below.
//
// CSR-1160 (A-001): the force-sync can now resolve to a terminal `removed`
// outcome — DC returned 404 for the insured's externalId, so the server
// soft-deleted this AMS row immediately. The response carries
// `status: "removed"` (and an empty `policies` array, so there is nothing to
// poll). The hook detects that variant in `onSuccess`, surfaces a clear toast,
// invalidates the queries that must reflect the removal (global search +
// insureds list/detail), and redirects away from the now-deleted detail page —
// it does NOT enter the polling path. The normal `status: "queued"` path is
// unchanged.
export type SyncInsuredPoliciesResult =
  | {
      status: "queued";
      fake: boolean;
      policies: Array<{
        policyId: string;
        policyNumber: string;
        queued: boolean;
        skipped: boolean;
        error?: string;
        errorCode?: number;
      }>;
    }
  | {
      status: "removed";
      removed: true;
      fake: boolean;
      policies: [];
    };

export function useSyncInsuredPolicies(insuredId: string) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [, navigate] = useLocation();
  return useMutation({
    mutationFn: async (): Promise<SyncInsuredPoliciesResult> => {
      // CSR-1129: key the sync by insured id (unique, URL-safe) so Refresh
      // resolves the same policy set the page loaded by id.
      const res = await apiFetch(
        `${API_BASE}/insureds/${encodeURIComponent(insuredId)}/sync`,
        { method: "POST", credentials: "include" },
      );
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        if (res.status === 503) {
          throw new Error("Carrier sync is paused on Data Center. Try again once it's running.");
        }
        throw new Error(body.error || "Failed to sync insured policies");
      }
      return res.json() as Promise<SyncInsuredPoliciesResult>;
    },
    onSuccess: (data) => {
      // CSR-1160 (A-001): terminal removal — do not poll. Surface the outcome,
      // drop the now-stale cards from search/list/detail, and route the user
      // off the deleted detail page so they don't linger on a 404 shell.
      if (data.status === "removed") {
        toast({
          title: "Client removed",
          description:
            "This client no longer exists in the Data Center and was removed.",
        });
        queryClient.invalidateQueries({ queryKey: ["global-search"] });
        queryClient.invalidateQueries({ queryKey: ["insureds"] });
        queryClient.invalidateQueries({ queryKey: ["insureds", "detail", insuredId] });
        queryClient.invalidateQueries({ queryKey: ["insureds-stats"] });
        invalidateActivityLogs(queryClient);
        navigate("/insureds");
        return;
      }
      queryClient.invalidateQueries({ queryKey: ["insured-policies-sync-status", insuredId] });
    },
  });
}

// CSR-1090: poll the batched "syncing now?" status for an insured's
// policies. Halts when none are syncing. Fires the cross-cutting
// query invalidations once on the any-syncing → none-syncing transition.
//
// CSR-1090 cycle 2: each per-policy entry now carries the most recent
// terminal `lastSyncOutcome` + `lastSyncError`. The caller
// (`InsuredSidebar`) is responsible for surfacing the destructive toast
// on the any-syncing → none-syncing transition when at least one policy
// in the snapshot reports `lastSyncOutcome === "failed"`.
export interface InsuredPolicySyncStatusEntry {
  policyId: string;
  policyNumber: string | null;
  syncing: boolean;
  lastSyncedAt: string | null;
  // CSR-1703: widened for type consistency with PolicySyncStatusResponse.
  // InsuredSidebar's destructive toast still keys off `=== "failed"` only
  // (a persistent banner, not a toast, is the chosen UX for `not_found`).
  lastSyncOutcome: PolicySyncOutcome | null;
  lastSyncError: string | null;
  // CSR-2174: `unattributable` is a success, so InsuredSidebar's `=== "failed"`
  // toast leaves it alone by construction. Carried here so the per-policy rows
  // can name the cause without a second fetch.
  lastSyncUnattributableReason: SyncAttemptUnavailableReason | null;
  lastSyncOutcomeAt: string | null;
}

export function useInsuredPoliciesSyncStatus(
  insuredId: string,
  policyIds: string[],
  enabled = true,
) {
  const queryClient = useQueryClient();
  const prevAnySyncingRef = useRef<boolean | null>(null);

  const query = useQuery<{ policies: InsuredPolicySyncStatusEntry[] }>({
    // CSR-1129: key by insured id (not name) so invalidation lines up with
    // the id-based sync mutation. Include the sorted policy ids in the query
    // key so the cache is keyed by the exact id set being watched.
    queryKey: ["insured-policies-sync-status", insuredId, [...policyIds].sort()],
    queryFn: async () => {
      const res = await apiFetch(
        `${API_BASE}/insureds/${encodeURIComponent(insuredId)}/sync-status`,
        {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ policyIds }),
          cache: "no-store",
        },
      );
      if (!res.ok) throw new Error("Failed to read insured sync status");
      return res.json();
    },
    enabled: enabled && !!insuredId && policyIds.length > 0,
    refetchInterval: (q) =>
      q.state.data?.policies.some((p) => p.syncing) ? 5000 : false,
    staleTime: 2_000,
  });

  useEffect(() => {
    if (query.data === undefined) return;
    const anySyncing = query.data.policies.some((p) => p.syncing);
    const wasAnySyncing = prevAnySyncingRef.current;
    prevAnySyncingRef.current = anySyncing;
    if (wasAnySyncing === true && anySyncing === false) {
      queryClient.invalidateQueries({ queryKey: ["policies"] });
      queryClient.invalidateQueries({ queryKey: ["insured-policies"] });
      queryClient.invalidateQueries({ queryKey: ["insureds"] });
      invalidateActivityLogs(queryClient);
    }
  }, [query.data, queryClient]);

  return query;
}

export function useSyncPolicies() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const response = await apiFetch(`${API_BASE}/sync/policies`, {
        method: "POST",
        credentials: "include",
      });
      if (!response.ok) throw new Error("Failed to sync policies");
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["policies"] });
    },
  });
}

interface SyncStatusEntry {
  lastSyncedAt: string | null;
  status: string;
  totalRecords: number;
  errorMessage?: string;
}

interface SyncStatusResponse {
  policies: SyncStatusEntry;
  insureds?: SyncStatusEntry;
}

export function useSyncStatus(enabled: boolean) {
  return useQuery<SyncStatusResponse>({
    queryKey: ["sync-status"],
    queryFn: async () => {
      const response = await apiFetch(`${API_BASE}/sync/status`, { credentials: "include" });
      if (!response.ok) throw new Error("Failed to fetch sync status");
      return response.json();
    },
    enabled,
    refetchInterval: enabled ? 1000 : false,
  });
}

// System settings hooks
export function useSystemSettings() {
  return useQuery<{ syncIntervalMinutes: number }>({
    queryKey: ["system-settings"],
    queryFn: async () => {
      const response = await apiFetch(`${API_BASE}/settings/system`, { credentials: "include" });
      if (!response.ok) throw new Error("Failed to fetch system settings");
      return response.json();
    },
  });
}

export function useUpdateSystemSettings() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (settings: { syncIntervalMinutes: number }) => {
      const response = await apiFetch(`${API_BASE}/settings/system`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(settings),
        credentials: "include",
      });
      if (!response.ok) throw new Error("Failed to update system settings");
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["system-settings"] });
    },
  });
}

// Activity logs API hooks
export function useActivityLogs(agencyId?: string | null, limit: number = 50) {
  return useQuery<ActivityLogWithUser[]>({
    queryKey: ["activity-logs", agencyId, limit],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (agencyId) params.append("agencyId", agencyId);
      params.append("limit", limit.toString());

      const response = await apiFetch(`${API_BASE}/activity-logs?${params.toString()}`, { credentials: "include" });
      if (!response.ok) throw new Error("Failed to fetch activity logs");
      return response.json();
    },
  });
}

// CSR-1445 — insured-scoped activity feed for the Insured Detail timeline.
// Replaces `useActivityLogs(agencyId, 100)` (global top-100 + client-side
// filter), which silently dropped an insured's events that fell outside the
// global most-recent window and (because insured logs are stamped to the
// nameless placeholder agency) hid them entirely under a specific-agency view.
// This endpoint filters server-side by the insured's own entity rows + its
// policies' rows, with no agency filter — so it works in both "All Agencies"
// and a specific-agency view.
export interface InsuredActivityLogsResponse {
  data: ActivityLogWithUser[];
  total: number;
  limit: number;
  offset: number;
}

export function useInsuredActivityLogs(
  insuredId: string | null,
  limit: number = 200,
) {
  return useQuery<InsuredActivityLogsResponse>({
    queryKey: ["insured-activity-logs", insuredId, limit],
    queryFn: async () => {
      const params = new URLSearchParams({ limit: limit.toString() });
      const response = await apiFetch(
        `${API_BASE}/insureds/${insuredId}/activity-logs?${params.toString()}`,
        { credentials: "include" },
      );
      if (!response.ok) throw new Error("Failed to fetch insured activity logs");
      return response.json();
    },
    enabled: !!insuredId,
  });
}

// Policy document hooks
export function usePolicyDocuments(policyId: string) {
  return useQuery<PolicyDocument[]>({
    queryKey: ["policy-documents", policyId],
    queryFn: async () => {
      const response = await apiFetch(`${API_BASE}/policies/${policyId}/documents`, { credentials: "include" });
      if (!response.ok) throw new Error("Failed to fetch documents");
      return response.json();
    },
    enabled: !!policyId,
  });
}

export function useUploadPolicyDocument(policyId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: {
      documentType: string;
      name: string;
      fileName: string;
      fileSize: number;
      mimeType: string;
      objectPath: string;
      notes?: string;
    }) => {
      const response = await apiFetch(`${API_BASE}/policies/${policyId}/documents`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
        credentials: "include",
      });
      if (!response.ok) throw new Error("Failed to save document");
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["policy-documents", policyId] });
    },
  });
}

export function useDeletePolicyDocument(policyId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const response = await apiFetch(`${API_BASE}/documents/${id}`, {
        method: "DELETE",
        credentials: "include",
      });
      if (!response.ok) throw new Error("Failed to delete document");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["policy-documents", policyId] });
    },
  });
}

// Insured document hooks (CSR-773).
// Two read hooks share the endpoint with a flag: the table reads only
// active rows; the timeline reads everything (including soft-deleted) so
// the corresponding `Document Uploaded` event survives the user-initiated
// delete. Distinct query keys keep the two caches isolated.
// CSR-1257: `policyNumber` is the linked policy's number (resolved server-side via
// LEFT JOIN), or null when no policy is linked. `policyId` comes via InsuredDocument.
export type InsuredDocumentRow = InsuredDocument & {
  uploadedByName: string | null;
  policyNumber: string | null;
  // CSR-2056 — every policy number the document covers (a generated ACORD form can
  // cover several). Resolved server-side; `policyNumber` above is the older
  // single-link field and is null for generated documents. Render this one.
  policyNumbers: string[];
};

export function useInsuredDocuments(insuredId: string | null) {
  return useQuery<InsuredDocumentRow[]>({
    queryKey: ["insured-documents", insuredId],
    queryFn: async () => {
      const response = await apiFetch(
        `${API_BASE}/insureds/${insuredId}/documents`,
        { credentials: "include" },
      );
      if (!response.ok) throw new Error("Failed to fetch insured documents");
      return response.json();
    },
    enabled: !!insuredId,
  });
}

export function useInsuredDocumentsForTimeline(insuredId: string | null) {
  return useQuery<InsuredDocumentRow[]>({
    queryKey: ["insured-documents", insuredId, "withDeleted"],
    queryFn: async () => {
      const response = await apiFetch(
        `${API_BASE}/insureds/${insuredId}/documents?includeDeleted=1`,
        { credentials: "include" },
      );
      if (!response.ok) throw new Error("Failed to fetch insured documents (timeline)");
      return response.json();
    },
    enabled: !!insuredId,
  });
}

export function useUploadInsuredDocument(insuredId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: {
      name: string;
      fileName: string;
      fileSize: number;
      mimeType: string;
      objectPath: string;
      policyId?: string | null;
    }) => {
      const response = await apiFetch(`${API_BASE}/insureds/${insuredId}/documents`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
        credentials: "include",
      });
      if (!response.ok) {
        // CSR-1807 — surface the server's error (e.g. the 503 DC-unavailable
        // message or the 413 size cap) instead of a generic string.
        const body = await response.json().catch(() => null);
        const fallback =
          response.status === 413
            ? "File is too large. Maximum size is 50 MB."
            : "Failed to save document";
        throw new Error(
          typeof body?.error === "string" && body.error ? body.error : fallback,
        );
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["insured-documents", insuredId] });
      queryClient.invalidateQueries({
        queryKey: ["insured-documents", insuredId, "withDeleted"],
      });
    },
  });
}

export function useDeleteInsuredDocument(insuredId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const response = await apiFetch(`${API_BASE}/documents/${id}`, {
        method: "DELETE",
        credentials: "include",
      });
      if (!response.ok) throw new Error("Failed to delete document");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["insured-documents", insuredId] });
      queryClient.invalidateQueries({
        queryKey: ["insured-documents", insuredId, "withDeleted"],
      });
    },
  });
}

// ─────────────────────────────────────────────────────────────────────
// Generated ACORD forms (CSR-2062 / CSR-1824)
//
// The prefill read belongs to neither modal: the selection modal (CSR-2062)
// starts it the moment the agent clicks Generate, and the editor modal
// (CSR-2063) is what renders the values. Both address it through the ONE cache
// key below, so the editor mounts on data that is already there instead of
// re-requesting it — and a retry inside the editor is a refetch of the same
// query rather than a second code path.
// ─────────────────────────────────────────────────────────────────────
export interface AcordPrefillRequest {
  insuredId: string;
  formId: AcordFormId;
  // In the agent's selection order — the server's single-slot rule fills a
  // one-blank field from the first policy (CSR-2057).
  policyIds: string[];
  // Keyed by the `extraFields[].key` the registry declares for the form.
  extras: Partial<Record<AcordExtraField["key"], string>>;
}

export interface AcordFormPrefill {
  displayName: string;
  // Empty for every form until the per-form maps land (CSR-2058..CSR-2061) —
  // expected, not a failure.
  text: Record<string, string>;
  checks: Record<string, boolean>;
}

export function acordFormPrefillQueryKey(request: AcordPrefillRequest) {
  return [
    "acord-form-prefill",
    request.insuredId,
    request.formId,
    request.policyIds,
    request.extras,
  ] as const;
}

export async function fetchAcordFormPrefill(
  request: AcordPrefillRequest,
): Promise<AcordFormPrefill> {
  const params = new URLSearchParams();
  // Repeated `policyIds` — the server reads them as an ordered list.
  for (const policyId of request.policyIds) params.append("policyIds", policyId);
  for (const [key, value] of Object.entries(request.extras)) {
    if (value) params.set(key, value);
  }
  const query = params.size > 0 ? `?${params.toString()}` : "";
  const response = await apiFetch(
    `${API_BASE}/insureds/${encodeURIComponent(request.insuredId)}/acord-forms/${
      encodeURIComponent(request.formId)
    }/prefill${query}`,
    { credentials: "include" },
  );
  if (!response.ok) {
    // The route answers a rejected selection with a specific message ("… requires
    // a policy", "Unknown ACORD form"); showing it beats a generic failure.
    const body = await response.json().catch(() => null);
    throw new ApiError(
      response.status,
      typeof body?.error === "string" && body.error
        ? body.error
        : "Failed to prepare the form. Please try again.",
    );
  }
  return response.json();
}

// CSR-2063 — the blank template the editor draws its pages from. Keyed by form
// alone (the paper does not vary by insured), so opening the same form twice in a
// session costs one request.
export function acordFormTemplateQueryKey(formId: AcordFormId) {
  return ["acord-form-template", formId] as const;
}

export async function fetchAcordFormTemplate(formId: AcordFormId): Promise<ArrayBuffer> {
  const response = await apiFetch(
    `${API_BASE}/acord-forms/${encodeURIComponent(formId)}/template`,
    { credentials: "include" },
  );
  if (!response.ok) {
    const body = await response.json().catch(() => null);
    throw new ApiError(
      response.status,
      typeof body?.error === "string" && body.error
        ? body.error
        : "Failed to load the form. Please try again.",
    );
  }
  return response.arrayBuffer();
}

// Every value the finished document carries. The server writes these EXACTLY as
// sent and never merges them with the prefill, so this is the whole form, not the
// agent's diff against it.
export interface AcordFormValues {
  text: Record<string, string>;
  checks: Record<string, boolean>;
  // Group name → the chosen option's export value. One group exists across all
  // eight templates (the RPS Cargo App's "Carrier Type").
  radios: Record<string, string>;
}

function acordDocumentBody(request: AcordPrefillRequest, values: AcordFormValues) {
  return {
    formId: request.formId,
    policyIds: request.policyIds,
    // The modal's date fields live at the top level, keyed as the registry
    // declares them — that is where `parseAcordExtras` reads them.
    ...request.extras,
    ...values,
  };
}

// Save — stores the document against the insured and returns the new row.
export function useSaveAcordDocument(insuredId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (args: { request: AcordPrefillRequest; values: AcordFormValues }) => {
      const response = await apiFetch(`${API_BASE}/insureds/${encodeURIComponent(insuredId)}/acord-documents`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(acordDocumentBody(args.request, args.values)),
        credentials: "include",
      });
      if (!response.ok) {
        const body = await response.json().catch(() => null);
        throw new ApiError(
          response.status,
          typeof body?.error === "string" && body.error
            ? body.error
            : "Failed to save document. Please try again.",
        );
      }
      return (await response.json()) as InsuredDocumentRow;
    },
    onSuccess: () => {
      // The same two caches the hand-upload path invalidates — the table and the
      // timeline's include-deleted read.
      queryClient.invalidateQueries({ queryKey: ["insured-documents", insuredId] });
      queryClient.invalidateQueries({
        queryKey: ["insured-documents", insuredId, "withDeleted"],
      });
    },
  });
}

// Download — the same values through the preview route, which stores nothing.
export async function fetchAcordDocumentPreview(
  insuredId: string,
  request: AcordPrefillRequest,
  values: AcordFormValues,
): Promise<Blob> {
  const response = await apiFetch(
    `${API_BASE}/insureds/${encodeURIComponent(insuredId)}/acord-documents/preview`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(acordDocumentBody(request, values)),
      credentials: "include",
    },
  );
  if (!response.ok) {
    const body = await response.json().catch(() => null);
    throw new ApiError(
      response.status,
      typeof body?.error === "string" && body.error
        ? body.error
        : "Failed to download document. Please try again.",
    );
  }
  return response.blob();
}

// Certificate Holder API hooks
// CSR-745: parametric paginated hook. The route now returns the
// envelope `{ data, total, page, pageSize }`. Every option flows into
// the TanStack Query key so distinct page/sort/q combinations cache
// independently. `placeholderData: keepPreviousData` keeps the previous
// page rendered during a fetch transition.
export interface CertificateHoldersQueryOptions {
  page?: number;
  pageSize?: number;
  q?: string;
  sort?: "name" | "usageCount" | "lastUsedAt" | "createdAt";
  sortDir?: "asc" | "desc";
  enabled?: boolean;
}

export function useCertificateHolders(options: CertificateHoldersQueryOptions = {}) {
  const { page, pageSize, q, sort, sortDir, enabled } = options;
  return useQuery<PaginatedResponse<CertificateHolder>>({
    queryKey: ["certificate-holders", { page, pageSize, q, sort, sortDir }],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (page != null) params.set("page", String(page));
      if (pageSize != null) params.set("pageSize", String(pageSize));
      if (q) params.set("q", q);
      if (sort) params.set("sort", sort);
      if (sortDir) params.set("sortDir", sortDir);
      const url =
        params.size > 0
          ? `${API_BASE}/certificate-holders?${params.toString()}`
          : `${API_BASE}/certificate-holders`;
      const response = await apiFetch(url, { credentials: "include" });
      if (!response.ok) throw new Error("Failed to fetch certificate holders");
      return response.json();
    },
    placeholderData: keepPreviousData,
    enabled: enabled !== false,
  });
}

// CSR-745: single-row read for the edit dialog.
export function useCertificateHolder(id: string | null | undefined) {
  return useQuery<CertificateHolder>({
    queryKey: ["certificate-holders", "detail", id],
    queryFn: async () => {
      const response = await apiFetch(`${API_BASE}/certificate-holders/${id}`, { credentials: "include" });
      if (!response.ok) throw new Error("Failed to fetch certificate holder");
      return response.json();
    },
    enabled: !!id,
  });
}

// CSR-745: thin wrapper for the picker autocomplete. Disabled until the
// query is at least 2 characters (matches the global search box gate
// in Layout.tsx). Returns `data` as `CertificateHolder[]` directly so
// the picker can render without unwrapping the envelope.
export function useSearchCertificateHolders(query: string, enabled: boolean = true) {
  const trimmed = query.trim();
  const result = useCertificateHolders({
    q: trimmed || undefined,
    pageSize: 10,
    page: 1,
    enabled: enabled && trimmed.length >= 2,
  });
  return { ...result, data: result.data?.data ?? [] };
}

export function useCreateCertificateHolder() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: InsertCertificateHolder) => {
      const response = await apiFetch(`${API_BASE}/certificate-holders`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
        credentials: "include",
      });
      if (!response.ok) throw new Error("Failed to create certificate holder");
      return response.json() as Promise<CertificateHolder>;
    },
    onSuccess: () => {
      // CSR-745: predicate-based invalidation hits every page/sort/q
      // variant of the parametric `useCertificateHolders` hook plus the
      // single-row `["certificate-holders", "detail", id]` entries.
      queryClient.invalidateQueries({ predicate: (q) => q.queryKey[0] === "certificate-holders" });
    },
  });
}

export function useUpdateCertificateHolder() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<InsertCertificateHolder> }) => {
      const response = await apiFetch(`${API_BASE}/certificate-holders/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
        credentials: "include",
      });
      if (!response.ok) throw new Error("Failed to update certificate holder");
      return response.json() as Promise<CertificateHolder>;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ predicate: (q) => q.queryKey[0] === "certificate-holders" });
    },
  });
}

export function useDeleteCertificateHolder() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const response = await apiFetch(`${API_BASE}/certificate-holders/${id}`, {
        method: "DELETE",
        credentials: "include",
      });
      if (!response.ok) throw new Error("Failed to delete certificate holder");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ predicate: (q) => q.queryKey[0] === "certificate-holders" });
    },
  });
}

// CSR-778: `useCOIHistory(policyId)` was removed. The COI is an insured-level
// document; use `useInsuredCOIHistory(insuredId)` below.

export type COIIssuanceWithUser = COIIssuance & {
  issuedByName: string | null;
};

export type COIHistoryFilters = {
  holder?: string;
  sentFrom?: string;
  sentTo?: string;
  sentBy?: string;
};

export type COIHistoryPage = {
  data: COIIssuanceWithUser[];
  total: number;
  page: number;
  pageSize: number;
};

function coiHistoryParams(filters?: COIHistoryFilters): URLSearchParams {
  const params = new URLSearchParams();
  if (filters?.holder) params.append("holder", filters.holder);
  if (filters?.sentFrom) params.append("sentFrom", filters.sentFrom);
  if (filters?.sentTo) params.append("sentTo", filters.sentTo);
  if (filters?.sentBy) params.append("sentBy", filters.sentBy);
  return params;
}

// CSR-758: poll every 5s while at least one issuance is still processing so
// the row auto-flips to 'sent' or 'failed' without a manual refresh.
// Returning `false` halts polling — React Query treats the false sentinel as
// "no interval".
const POLL_WHILE_PROCESSING_MS = 5000;

function pollWhileProcessing(rows: COIIssuanceWithUser[] | undefined) {
  return rows?.some((i) => i.deliveryStatus === "processing")
    ? POLL_WHILE_PROCESSING_MS
    : false;
}

/** The WHOLE history, unpaginated — the shape this endpoint has always had.
 *  Used by the insured activity timeline, which builds one card per issuance
 *  and so needs every row. The COIs tab pages instead: see
 *  `useInsuredCOIHistoryPage`. */
export function useInsuredCOIHistory(insuredId: string, filters?: COIHistoryFilters) {
  return useQuery<COIIssuanceWithUser[]>({
    queryKey: ["insured-coi-history", insuredId, filters],
    queryFn: async () => {
      const params = coiHistoryParams(filters);
      const response = await apiFetch(`${API_BASE}/insureds/${insuredId}/coi-history?${params.toString()}`, { credentials: "include" });
      if (!response.ok) throw new Error("Failed to fetch insured COI history");
      return response.json();
    },
    enabled: !!insuredId,
    refetchInterval: (query) =>
      pollWhileProcessing(query.state.data as COIIssuanceWithUser[] | undefined),
  });
}

/** CSR-2237: one page of the history. A book of certificates only grows, and
 *  the tab used to render every row it had — the largest in production is over
 *  a hundred. Sending `page`/`pageSize` is what asks the server for the
 *  `{ data, total, page, pageSize }` envelope; without them it answers with the
 *  bare array above. */
export function useInsuredCOIHistoryPage(
  insuredId: string,
  page: number,
  pageSize: number,
  filters?: COIHistoryFilters,
) {
  return useQuery<COIHistoryPage>({
    queryKey: ["insured-coi-history-page", insuredId, page, pageSize, filters],
    queryFn: async () => {
      const params = coiHistoryParams(filters);
      params.set("page", String(page));
      params.set("pageSize", String(pageSize));
      const response = await apiFetch(`${API_BASE}/insureds/${insuredId}/coi-history?${params.toString()}`, { credentials: "include" });
      if (!response.ok) throw new Error("Failed to fetch insured COI history");
      return response.json();
    },
    enabled: !!insuredId,
    // Without this the table empties to its loading spinner on every page
    // step, which reads as "there is nothing here" rather than "the next page
    // is coming".
    placeholderData: keepPreviousData,
    refetchInterval: (query) => pollWhileProcessing(query.state.data?.data),
  });
}

export function useInsuredCOISenders(insuredId: string) {
  return useQuery<{ id: string; name: string }[]>({
    queryKey: ["insured-coi-senders", insuredId],
    queryFn: async () => {
      const response = await apiFetch(`${API_BASE}/insureds/${insuredId}/coi-senders`, { credentials: "include" });
      if (!response.ok) throw new Error("Failed to fetch COI senders");
      return response.json();
    },
    enabled: !!insuredId,
  });
}

export function useInsuredCoiNotes(insuredId: string) {
  return useQuery<InsuredCoiNotes | null>({
    queryKey: ["insured-coi-notes", insuredId],
    queryFn: async () => {
      const response = await apiFetch(`${API_BASE}/insureds/${insuredId}/coi-notes`, { credentials: "include" });
      if (!response.ok) throw new Error("Failed to fetch insured COI notes");
      return response.json();
    },
    enabled: !!insuredId,
  });
}

export function useUpsertInsuredCoiNotes() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ insuredId, coiNotes }: { insuredId: string; coiNotes: Array<CoiNote> }) => {
      const response = await apiFetch(`${API_BASE}/insureds/${insuredId}/coi-notes`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ coiNotes }),
        credentials: "include",
      });
      if (!response.ok) throw new Error("Failed to update insured COI notes");
      return response.json();
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["insured-coi-notes", variables.insuredId] });
      invalidateActivityLogs(queryClient);
    },
  });
}

// Additional Insureds API hooks
export interface InsuredAdditionalInsuredLink {
  linkId: string;
  id: string;
  name: string;
  email: string | null;
  street: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  fullAddress: string | null;
  coiCount: number;
  lastSentAt: string | null;
  coverageIds: string[];
  specialVerbiage: string | null;
  createdAt: string;
  // CSR-2274 — additive; absent on responses from a COI service older than
  // this release. Optional so `undefined` behaves exactly like `false`.
  inactive?: boolean;
}

export function useAdditionalInsureds(insuredId: string) {
  return useQuery<InsuredAdditionalInsuredLink[]>({
    queryKey: ["additional-insureds", insuredId],
    queryFn: async () => {
      const response = await apiFetch(`${API_BASE}/insureds/${insuredId}/additional-insureds`, { credentials: "include" });
      if (!response.ok) throw new Error("Failed to fetch additional insureds");
      return response.json();
    },
    enabled: !!insuredId,
  });
}

export function useCreateAdditionalInsured(insuredId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: {
      certificateHolderId?: string;
      name?: string;
      email?: string;
      street?: string;
      city?: string;
      state?: string;
      zip?: string;
      coverageIds?: string[];
      specialVerbiage?: string | null;
    }) => {
      const response = await apiFetch(`${API_BASE}/insureds/${insuredId}/additional-insureds`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
        credentials: "include",
      });
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.error || "Failed to create additional insured");
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["additional-insureds", insuredId] });
      invalidateActivityLogs(queryClient);
    },
  });
}

export function useUpdateAdditionalInsured(insuredId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ linkId, data }: { linkId: string; data: Record<string, unknown> }) => {
      const response = await apiFetch(`${API_BASE}/insureds/${insuredId}/additional-insureds/${linkId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
        credentials: "include",
      });
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.error || "Failed to update additional insured");
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["additional-insureds", insuredId] });
      invalidateActivityLogs(queryClient);
    },
  });
}

export function useDeleteAdditionalInsured(insuredId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (linkId: string) => {
      const response = await apiFetch(`${API_BASE}/insureds/${insuredId}/additional-insureds/${linkId}`, {
        method: "DELETE",
        credentials: "include",
      });
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.error || "Failed to delete additional insured");
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["additional-insureds", insuredId] });
      invalidateActivityLogs(queryClient);
    },
  });
}

// CSR-792 — Loss Payee API hooks. Mirrors the AI block above but reads
// from `/api/insureds/:id/loss-payees`. The query key is `["loss-payees",
// insuredId]` and every mutation invalidates `["loss-payees", insuredId]`
// + `["activity-logs"]`.
export interface LossPayeeLinkVehicle {
  policyId: string;
  vin: string;
  label: string;
  raw: import("@shared/schema").PolicyVehicle;
}

export interface LossPayeeLink {
  id: string;
  insuredId: string;
  name: string;
  email: string;
  street: string;
  city: string;
  state: string;
  zip: string;
  fullAddress: string;
  specialVerbiage: string | null;
  pendingAdditionalInfo: boolean;
  coverageIds: string[];
  vehicles: LossPayeeLinkVehicle[];
  // CSR-2212 — null means the COI service could not be reached, so the count is
  // UNKNOWN, not zero. The Assets tab renders an em dash for it: a zero here
  // would read as "never sent" and prompt a duplicate certificate. Authorized
  // widening of an /api/* response field; see `CoiPartyStats` in
  // server/coi/storage.ts for the approval and the consumer enumeration.
  coiCount: number | null;
  lastSentAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateLossPayeeBody {
  name: string;
  email: string;
  street: string;
  city: string;
  state: string;
  zip: string;
  specialVerbiage?: string | null;
  coverageIds: string[];
  // CSR-1855 — `assetKey` (keyForVehicle identity) is the stored link key so
  // VIN-less assets can be linked; `vin` is optional metadata. Response type
  // (`LossPayeeLinkVehicle`) is unchanged.
  vehicles?: Array<{ policyId: string; assetKey: string; vin?: string }>;
}

export type UpdateLossPayeeBody = Partial<CreateLossPayeeBody>;

export function useLossPayees(insuredId: string) {
  return useQuery<LossPayeeLink[]>({
    queryKey: ["loss-payees", insuredId],
    queryFn: async () => {
      const response = await apiFetch(
        `${API_BASE}/insureds/${insuredId}/loss-payees`,
        { credentials: "include" },
      );
      if (!response.ok) throw new Error("Failed to fetch loss payees");
      return response.json();
    },
    enabled: !!insuredId,
  });
}

export function useCreateLossPayee(insuredId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: CreateLossPayeeBody) => {
      const response = await apiFetch(
        `${API_BASE}/insureds/${insuredId}/loss-payees`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(data),
          credentials: "include",
        },
      );
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.error || "Failed to create loss payee");
      }
      return response.json() as Promise<LossPayeeLink>;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["loss-payees", insuredId] });
      invalidateActivityLogs(queryClient);
    },
  });
}

export function useUpdateLossPayee(insuredId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ lpId, data }: { lpId: string; data: UpdateLossPayeeBody }) => {
      const response = await apiFetch(
        `${API_BASE}/insureds/${insuredId}/loss-payees/${lpId}`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(data),
          credentials: "include",
        },
      );
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.error || "Failed to update loss payee");
      }
      return response.json() as Promise<LossPayeeLink>;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["loss-payees", insuredId] });
      invalidateActivityLogs(queryClient);
    },
  });
}

export function useDeleteLossPayee(insuredId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (lpId: string) => {
      const response = await apiFetch(
        `${API_BASE}/insureds/${insuredId}/loss-payees/${lpId}`,
        {
          method: "DELETE",
          credentials: "include",
        },
      );
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.error || "Failed to delete loss payee");
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["loss-payees", insuredId] });
      invalidateActivityLogs(queryClient);
    },
  });
}

// CSR-1101 — Waivers of Subrogation. Mirror of the Loss Payee hooks minus the
// `vehicles` field; `email` is nullable.
export interface WaiverOfSubrogationLink {
  id: string;
  insuredId: string;
  name: string;
  email: string | null;
  street: string;
  city: string;
  state: string;
  zip: string;
  fullAddress: string;
  specialVerbiage: string | null;
  pendingAdditionalInfo: boolean;
  coverageIds: string[];
  // CSR-2212 — null means the COI service could not be reached, so the count is
  // UNKNOWN, not zero. The Assets tab renders an em dash for it: a zero here
  // would read as "never sent" and prompt a duplicate certificate. Authorized
  // widening of an /api/* response field; see `CoiPartyStats` in
  // server/coi/storage.ts for the approval and the consumer enumeration.
  coiCount: number | null;
  lastSentAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateWaiverOfSubrogationBody {
  name: string;
  email?: string | null;
  street: string;
  city: string;
  state: string;
  zip: string;
  specialVerbiage?: string | null;
  coverageIds: string[];
}

export type UpdateWaiverOfSubrogationBody = Partial<CreateWaiverOfSubrogationBody>;

export function useWaiversOfSubrogation(insuredId: string) {
  return useQuery<WaiverOfSubrogationLink[]>({
    queryKey: ["waivers-of-subrogation", insuredId],
    queryFn: async () => {
      const response = await apiFetch(
        `${API_BASE}/insureds/${insuredId}/waivers-of-subrogation`,
        { credentials: "include" },
      );
      if (!response.ok) throw new Error("Failed to fetch waivers of subrogation");
      return response.json();
    },
    enabled: !!insuredId,
  });
}

export function useCreateWaiverOfSubrogation(insuredId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: CreateWaiverOfSubrogationBody) => {
      const response = await apiFetch(
        `${API_BASE}/insureds/${insuredId}/waivers-of-subrogation`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(data),
          credentials: "include",
        },
      );
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.error || "Failed to create waiver of subrogation");
      }
      return response.json() as Promise<WaiverOfSubrogationLink>;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["waivers-of-subrogation", insuredId] });
      invalidateActivityLogs(queryClient);
    },
  });
}

export function useUpdateWaiverOfSubrogation(insuredId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ wosId, data }: { wosId: string; data: UpdateWaiverOfSubrogationBody }) => {
      const response = await apiFetch(
        `${API_BASE}/insureds/${insuredId}/waivers-of-subrogation/${wosId}`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(data),
          credentials: "include",
        },
      );
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.error || "Failed to update waiver of subrogation");
      }
      return response.json() as Promise<WaiverOfSubrogationLink>;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["waivers-of-subrogation", insuredId] });
      invalidateActivityLogs(queryClient);
    },
  });
}

export function useDeleteWaiverOfSubrogation(insuredId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (wosId: string) => {
      const response = await apiFetch(
        `${API_BASE}/insureds/${insuredId}/waivers-of-subrogation/${wosId}`,
        {
          method: "DELETE",
          credentials: "include",
        },
      );
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.error || "Failed to delete waiver of subrogation");
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["waivers-of-subrogation", insuredId] });
      invalidateActivityLogs(queryClient);
    },
  });
}

// CSR-1658 — Commodities. Global catalog + per-insured links. Mutations
// invalidate both the insured list AND the catalog (adding a new commodity
// grows the shared catalog) plus the activity log.
export function useCommodities() {
  return useQuery<Commodity[]>({
    queryKey: ["commodities"],
    queryFn: async () => {
      const response = await apiFetch(`${API_BASE}/commodities`, {
        credentials: "include",
      });
      if (!response.ok) throw new Error("Failed to fetch commodities");
      return response.json();
    },
  });
}

export function useInsuredCommodities(insuredId: string) {
  return useQuery<InsuredCommodityLink[]>({
    queryKey: ["insured-commodities", insuredId],
    queryFn: async () => {
      const response = await apiFetch(
        `${API_BASE}/insureds/${insuredId}/commodities`,
        { credentials: "include" },
      );
      if (!response.ok) throw new Error("Failed to fetch insured commodities");
      return response.json();
    },
    enabled: !!insuredId,
  });
}

export function useAddInsuredCommodity(insuredId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (name: string) => {
      const response = await apiFetch(
        `${API_BASE}/insureds/${insuredId}/commodities`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ name }),
          credentials: "include",
        },
      );
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.error || "Failed to add commodity");
      }
      return response.json() as Promise<InsuredCommodityLink>;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["insured-commodities", insuredId] });
      queryClient.invalidateQueries({ queryKey: ["commodities"] });
      invalidateActivityLogs(queryClient);
    },
  });
}

export function useRemoveInsuredCommodity(insuredId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (linkId: string) => {
      const response = await apiFetch(
        `${API_BASE}/insureds/${insuredId}/commodities/${linkId}`,
        { method: "DELETE", credentials: "include" },
      );
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.error || "Failed to remove commodity");
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["insured-commodities", insuredId] });
      invalidateActivityLogs(queryClient);
    },
  });
}

// Policy Change History API hooks
export interface PolicyChangeHistoryEntry {
  id: string;
  policyId: string;
  field: string;
  oldValue: string | null;
  newValue: string | null;
  source: string;
  changedBy: string | null;
  changedByName: string | null;
  note: string | null;
  changedAt: string;
  // CSR-1842 — additive, present only on DC-sourced rows (flag ON): the raw
  // DC trigger source (carrier_scrape/status_sweep/ams_manual/flint/nowcerts)
  // and the raw DC actor. Rendering is CSR-1843's job.
  dcSource?: string;
  actor?: string | null;
}

// CSR-1843 — shared fetcher so the Insured Overview timeline's per-policy
// `useQueries` fan-out and this hook share one fetch + the same query key
// (`["policy-change-history", policyId]`), keeping the cache warm across surfaces.
export async function fetchPolicyChangeHistory(
  policyId: string,
): Promise<PolicyChangeHistoryEntry[]> {
  const response = await apiFetch(`${API_BASE}/policies/${policyId}/change-history`, {
    credentials: "include",
  });
  if (!response.ok) throw new Error("Failed to fetch change history");
  return response.json();
}

export function usePolicyChangeHistory(policyId: string) {
  return useQuery<PolicyChangeHistoryEntry[]>({
    queryKey: ["policy-change-history", policyId],
    queryFn: () => fetchPolicyChangeHistory(policyId),
    enabled: !!policyId,
  });
}

// CSR-1637 — per-policy activity-log feed: manual child-entity changes
// (Additional Insured / Loss Payee / Waiver of Subrogation) that reference this
// policy. The Policy Activities tab merges these with its native sources.
export function usePolicyActivityLogs(policyId: string, limit: number = 200) {
  return useQuery<InsuredActivityLogsResponse>({
    queryKey: ["policy-activity-logs", policyId, limit],
    queryFn: async () => {
      const params = new URLSearchParams({ limit: limit.toString() });
      const response = await apiFetch(
        `${API_BASE}/policies/${policyId}/activity-logs?${params.toString()}`,
        { credentials: "include" },
      );
      if (!response.ok) throw new Error("Failed to fetch policy activity logs");
      return response.json();
    },
    enabled: !!policyId,
  });
}

// CSR-1703 — policy-scoped sync-attempt feed (local `synced` activity-log
// rows, newest-first). Backs the "Sync" lane in PolicyActivitiesTab.
export interface PolicySyncEvent {
  id: string;
  outcome: PolicySyncOutcome;
  error: string | null;
  // CSR-2174 — present only on `unattributable`; null when DC named a reason
  // this build doesn't know, or on any row written before CSR-2174.
  unattributableReason: SyncAttemptUnavailableReason | null;
  removed: boolean;
  occurredAt: string;
  performedBy: string | null;
}

// CSR-1940 — shared fetcher so the Insured Overview timeline's per-policy
// `useQueries` fan-out and this hook share one fetch + the same query key
// (`["policy-sync-events", policyId]`), keeping the cache warm across surfaces.
// Mirrors `fetchPolicyChangeHistory`.
export async function fetchPolicySyncEvents(
  policyId: string,
): Promise<PolicySyncEvent[]> {
  const response = await apiFetch(`${API_BASE}/policies/${policyId}/sync-events`, {
    credentials: "include",
  });
  if (!response.ok) throw new Error("Failed to fetch policy sync events");
  return response.json();
}

export function usePolicySyncEvents(policyId: string) {
  return useQuery<PolicySyncEvent[]>({
    queryKey: ["policy-sync-events", policyId],
    queryFn: () => fetchPolicySyncEvents(policyId),
    enabled: !!policyId,
  });
}

// CSR-1955 — shared fetcher (hook + insured timeline fan-out share the query
// key ["policy-record-updates", policyId]). Mirrors fetchPolicyChangeHistory.
export async function fetchPolicyRecordUpdates(
  policyId: string,
): Promise<PolicyRecordUpdateRow[]> {
  const response = await apiFetch(`${API_BASE}/policies/${policyId}/record-updates`, {
    credentials: "include",
  });
  if (!response.ok) throw new Error("Failed to fetch policy record updates");
  return response.json();
}

export function usePolicyRecordUpdates(policyId: string) {
  return useQuery<PolicyRecordUpdateRow[]>({
    queryKey: ["policy-record-updates", policyId],
    queryFn: () => fetchPolicyRecordUpdates(policyId),
    enabled: !!policyId,
  });
}

// CSR-771 — Notes hooks (revised). Replaces useNotes / useCreateNote.

/** Server response shape for note rows (Note + joined fields). */
export type NoteRow = Note & {
  policyNumber: string | null;
  authorDisplayNameResolved: string;
};

/** GET /api/insureds/:insuredId/notes — all notes scoped to an insured (excludes soft-deleted). */
export function useInsuredNotes(insuredId: string) {
  return useQuery<NoteRow[]>({
    queryKey: ["notes", "insured", insuredId],
    queryFn: async () => {
      const r = await apiFetch(`${API_BASE}/insureds/${insuredId}/notes`, {
        credentials: "include",
      });
      if (!r.ok) throw new Error("Failed to fetch insured notes");
      return r.json();
    },
    enabled: !!insuredId,
  });
}

/** GET /api/policies/:policyId/notes — AMS notes scoped to a policy (excludes soft-deleted + NowCerts). */
export function usePolicyNotes(policyId: string) {
  return useQuery<NoteRow[]>({
    queryKey: ["notes", "policy", policyId],
    queryFn: async () => {
      const r = await apiFetch(`${API_BASE}/policies/${policyId}/notes`, {
        credentials: "include",
      });
      if (!r.ok) throw new Error("Failed to fetch policy notes");
      return r.json();
    },
    enabled: !!policyId,
  });
}

/** POST /api/insureds/:insuredId/notes — optimistic prepend on success. */
export function useCreateInsuredNote(insuredId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: { title: string; details?: string | null }) => {
      const r = await apiFetch(`${API_BASE}/insureds/${insuredId}/notes`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
        credentials: "include",
      });
      if (!r.ok) {
        const errBody = await r.json().catch(() => ({}));
        throw Object.assign(new Error("Failed to create note"), {
          status: r.status,
          body: errBody,
        });
      }
      return (await r.json()) as NoteRow;
    },
    onSuccess: (created) => {
      qc.setQueryData<NoteRow[]>(["notes", "insured", insuredId], (prev) =>
        prev ? [created, ...prev] : [created],
      );
      invalidateActivityLogs(qc);
    },
  });
}

/** POST /api/policies/:policyId/notes — also prepends to the parent insured cache. */
export function useCreatePolicyNote(policyId: string, insuredId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: { title: string; details?: string | null }) => {
      const r = await apiFetch(`${API_BASE}/policies/${policyId}/notes`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
        credentials: "include",
      });
      if (!r.ok) {
        const errBody = await r.json().catch(() => ({}));
        throw Object.assign(new Error("Failed to create note"), {
          status: r.status,
          body: errBody,
        });
      }
      return (await r.json()) as NoteRow;
    },
    onSuccess: (created) => {
      qc.setQueryData<NoteRow[]>(["notes", "policy", policyId], (prev) =>
        prev ? [created, ...prev] : [created],
      );
      qc.setQueryData<NoteRow[]>(["notes", "insured", insuredId], (prev) =>
        prev ? [created, ...prev] : [created],
      );
      invalidateActivityLogs(qc);
    },
  });
}

/**
 * PATCH /api/notes/:noteId — invalidates BOTH insured AND policy caches when applicable.
 * The hook reads the returned row's insuredId/policyId so call sites do not have to pass them in.
 */
export function useEditNote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: {
      noteId: string;
      title?: string;
      details?: string | null;
    }) => {
      const { noteId, ...patch } = args;
      const r = await apiFetch(`${API_BASE}/notes/${noteId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(patch),
        credentials: "include",
      });
      if (!r.ok) {
        const errBody = await r.json().catch(() => ({}));
        throw Object.assign(new Error("Failed to edit note"), {
          status: r.status,
          body: errBody,
        });
      }
      return (await r.json()) as NoteRow;
    },
    onSuccess: (updated) => {
      qc.invalidateQueries({ queryKey: ["notes", "insured", updated.insuredId] });
      if (updated.policyId) {
        qc.invalidateQueries({ queryKey: ["notes", "policy", updated.policyId] });
      }
      invalidateActivityLogs(qc);
    },
  });
}

/**
 * DELETE /api/notes/:noteId — soft-delete; invalidates BOTH caches when applicable.
 * The caller passes insuredId / policyId because the response body is just `{ deleted: true }`.
 */
export function useDeleteNote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: {
      noteId: string;
      insuredId: string;
      policyId: string | null;
    }) => {
      const r = await apiFetch(`${API_BASE}/notes/${args.noteId}`, {
        method: "DELETE",
        credentials: "include",
      });
      if (!r.ok) {
        const errBody = await r.json().catch(() => ({}));
        throw Object.assign(new Error("Failed to delete note"), {
          status: r.status,
          body: errBody,
        });
      }
      return args;
    },
    onSuccess: (vars) => {
      qc.invalidateQueries({ queryKey: ["notes", "insured", vars.insuredId] });
      if (vars.policyId) {
        qc.invalidateQueries({ queryKey: ["notes", "policy", vars.policyId] });
      }
      invalidateActivityLogs(qc);
    },
  });
}

/**
 * PATCH /api/notes/:noteId/pin — CSR-1885. Toggles the global pin and, like
 * useEditNote, invalidates BOTH insured AND policy caches from the returned row
 * so both timeline surfaces reflect the new pinned order.
 */
export function useTogglePinNote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { noteId: string; pinned: boolean }) => {
      const r = await apiFetch(`${API_BASE}/notes/${args.noteId}/pin`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ pinned: args.pinned }),
        credentials: "include",
      });
      if (!r.ok) {
        const errBody = await r.json().catch(() => ({}));
        throw Object.assign(new Error("Failed to pin note"), {
          status: r.status,
          body: errBody,
        });
      }
      return (await r.json()) as NoteRow;
    },
    onSuccess: (updated) => {
      qc.invalidateQueries({ queryKey: ["notes", "insured", updated.insuredId] });
      if (updated.policyId) {
        qc.invalidateQueries({ queryKey: ["notes", "policy", updated.policyId] });
      }
    },
  });
}

// COI Request API hooks
//
// CSR-867: `source` is widened to include the synthetic `"ams"` value at
// the wire-shape boundary. The shared `COIRequest` type (derived from
// `coi_requests.$inferSelect`) keeps the strict 4-value enum because
// `"ams"` is never persisted — it is only assigned to rows synthesized
// at read-time from orphan `coi_issuances` (see `storage.getCOIRequests`).
export type COIRequestWithEmail = Omit<COIRequest, "source"> & {
  source: COIRequest["source"] | "ams";
  inboundEmail: InboundEmail | null;
  documentId?: string | null;
  // CSR-674: joined from policies via getCOIRequests left-join. Avoids
  // loading the bulk policies list to render the "Policy Match" column.
  policyNumber?: string | null;
  policyCustomerName?: string | null;
  // CSR-867: joined from `users` via `coi_issuances.issued_by`. Null when
  // the request has no associated issuance yet, or when the issuance was
  // created by an Intercom auto-send with no human sender.
  issuedByName?: string | null;
  // CSR-1602: "recovered from forwarded email" provenance derived server-side
  // from the linked Intercom event (CSR-1582 marker). Null for the common case.
  forwardedRecovery?: ForwardedRecoveryMeta | null;
};

export interface COIRequestsQueryOptions {
  agencyId?: string | null;
  status?: string;
  source?: COIRequestSource;
  sentFilter?: COISentFilter;
  notSentReasons?: COINotSentReason[];
  // CSR-867 cycle 5 — `Created At` user-facing date range. Strings in
  // strict ISO `yyyy-mm-dd` form (the value emitted by
  // `<input type="date">`). Independently optional; the server treats
  // both ends as inclusive.
  createdAtFrom?: string;
  createdAtTo?: string;
  q?: string;
  page?: number;
  pageSize?: number;
  sort?: COIRequestSortField;
  sortDir?: SortDir;
  enabled?: boolean;
}

export type COIRequestsResponse = PaginatedResponse<COIRequestWithEmail>;

export function useCOIRequests(options: COIRequestsQueryOptions | string = {}) {
  const resolved: COIRequestsQueryOptions =
    typeof options === "string" ? { status: options } : options;
  const {
    agencyId,
    status,
    source,
    sentFilter,
    notSentReasons,
    createdAtFrom,
    createdAtTo,
    q,
    page,
    pageSize,
    sort,
    sortDir,
    enabled,
  } = resolved;
  const sortedNotSentReasons = notSentReasons ? [...notSentReasons].sort() : undefined;
  return useQuery<COIRequestsResponse>({
    queryKey: [
      "coi-requests",
      {
        agencyId,
        status,
        source,
        sentFilter,
        notSentReasons: sortedNotSentReasons?.join(",") ?? "",
        createdAtFrom,
        createdAtTo,
        q,
        page,
        pageSize,
        sort,
        sortDir,
      },
    ],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (agencyId) params.set("agencyId", agencyId);
      if (status) params.set("status", status);
      if (source) params.set("source", source);
      if (sentFilter) params.set("sentFilter", sentFilter);
      if (sortedNotSentReasons && sortedNotSentReasons.length > 0) {
        for (const r of sortedNotSentReasons) params.append("notSentReason", r);
      }
      if (createdAtFrom) params.set("createdAtFrom", createdAtFrom);
      if (createdAtTo) params.set("createdAtTo", createdAtTo);
      if (q) params.set("q", q);
      if (page != null) params.set("page", String(page));
      if (pageSize != null) params.set("pageSize", String(pageSize));
      if (sort) params.set("sort", sort);
      if (sortDir) params.set("sortDir", sortDir);
      const url = params.size > 0
        ? `${API_BASE}/coi-requests?${params.toString()}`
        : `${API_BASE}/coi-requests`;
      const response = await apiFetch(url, { credentials: "include" });
      if (!response.ok) throw new Error("Failed to fetch COI requests");
      return response.json();
    },
    placeholderData: keepPreviousData,
    enabled: enabled !== false,
    // CSR-2100: the carrier-wait statuses are the only ones that change with
    // nobody in this tab doing anything — logrock-coi flips them when the
    // carrier answers. Without this a row keeps showing the waiting icon after
    // the certificate has gone out, which the amber makes worse than the old
    // cross did: it actively claims the row is moving.
    //
    // Keyed on the status and nothing else, so it runs until the SERVER
    // reports something other than a carrier wait — including for a wait long
    // past the ten minutes after which the reason column stops vouching for
    // it. That column is saying what to show a person, not that the request is
    // dead; the service can still settle it, and this poll is how the tab
    // would find out.
    refetchInterval: (query) =>
      (query.state.data?.data ?? []).some((r) => isAwaitingCarrier(r.status))
        ? 30_000
        : false,
  });
}

export type COIRequestsStats = {
  total: number;
  sent: number;
  notSent: number;
  sources: number;
};

/** CSR-2100: `refreshWhileWaiting` — the caller passes true while the visible
 *  page holds a carrier wait. The counts move when that wait ends, and this
 *  query has no way to know that on its own. */
export function useCOIRequestsStats(
  agencyId?: string | null,
  opts?: { refreshWhileWaiting?: boolean },
) {
  return useQuery<COIRequestsStats>({
    queryKey: ["coi-requests-stats", agencyId],
    refetchInterval: opts?.refreshWhileWaiting ? 30_000 : false,
    queryFn: async () => {
      const url = agencyId
        ? `${API_BASE}/coi-requests/stats?agencyId=${agencyId}`
        : `${API_BASE}/coi-requests/stats`;
      const response = await apiFetch(url, { credentials: "include" });
      if (!response.ok) throw new Error("Failed to fetch COI requests stats");
      return response.json();
    },
  });
}

// CSR-1349: send-duration aggregates per send origin. All four group keys
// are always present; aggregates are null when a group has no qualifying
// sends in the window (the UI renders "--").
export type CoiSendMetricGroup = {
  count: number;
  avgSeconds: number | null;
  p50Seconds: number | null;
  p90Seconds: number | null;
  p95Seconds: number | null;
};

export type CoiSendMetrics = {
  windowDays: number;
  groups: {
    manual: CoiSendMetricGroup;
    coi_form: CoiSendMetricGroup;
    intercom_webhook: CoiSendMetricGroup;
    combined: CoiSendMetricGroup;
  };
};

export function useCoiSendMetrics(windowDays = 30) {
  return useQuery<CoiSendMetrics>({
    queryKey: ["coi-send-metrics", windowDays],
    queryFn: async () => {
      const response = await apiFetch(
        `${API_BASE}/coi-metrics/send-times?windowDays=${windowDays}`,
        { credentials: "include" },
      );
      if (!response.ok) throw new Error("Failed to fetch COI send-time metrics");
      return response.json();
    },
  });
}

// CSR-1349: per-event send durations backing the scatter chart — one row
// per qualifying send, ascending by sentAt, capped at `limit` most recent
// (`truncated` discloses when the window held more).
export type CoiSendEvent = {
  id: string;
  certificateHolderName: string;
  insuredName: string | null;
  sendOrigin: string;
  durationSeconds: number;
  syncTriggered: boolean | null;
  sentAt: string;
};

export type CoiSendEvents = {
  windowDays: number;
  limit: number;
  truncated: boolean;
  events: CoiSendEvent[];
};

export function useCoiSendEvents(windowDays = 30) {
  return useQuery<CoiSendEvents>({
    queryKey: ["coi-send-events", windowDays],
    queryFn: async () => {
      const response = await apiFetch(
        `${API_BASE}/coi-metrics/send-events?windowDays=${windowDays}`,
        { credentials: "include" },
      );
      if (!response.ok) throw new Error("Failed to fetch COI send events");
      return response.json();
    },
  });
}

/**
 * CSR-867: Build the URL for the CSV export endpoint, applying the same
 * filter query params as `useCOIRequests`. The browser fetches the URL
 * directly so the `Content-Disposition: attachment` header triggers a
 * file download without an extra blob-handling layer.
 */
export function buildCOIRequestsExportUrl(
  options: Omit<COIRequestsQueryOptions, "enabled" | "page" | "pageSize">,
): string {
  const {
    agencyId,
    status,
    source,
    sentFilter,
    notSentReasons,
    createdAtFrom,
    createdAtTo,
    q,
    sort,
    sortDir,
  } = options;
  const params = new URLSearchParams();
  if (agencyId) params.set("agencyId", agencyId);
  if (status) params.set("status", status);
  if (source) params.set("source", source);
  if (sentFilter) params.set("sentFilter", sentFilter);
  if (notSentReasons && notSentReasons.length > 0) {
    const sorted = [...notSentReasons].sort();
    for (const r of sorted) params.append("notSentReason", r);
  }
  if (createdAtFrom) params.set("createdAtFrom", createdAtFrom);
  if (createdAtTo) params.set("createdAtTo", createdAtTo);
  if (q) params.set("q", q);
  if (sort) params.set("sort", sort);
  if (sortDir) params.set("sortDir", sortDir);
  return params.size > 0
    ? `${API_BASE}/coi-requests/export?${params.toString()}`
    : `${API_BASE}/coi-requests/export`;
}

export function useCOIRequest(id: string) {
  return useQuery<COIRequestWithEmail>({
    queryKey: ["coi-request", id],
    queryFn: async () => {
      const response = await apiFetch(`${API_BASE}/coi-requests/${id}`, { credentials: "include" });
      if (!response.ok) throw new Error("Failed to fetch COI request");
      return response.json();
    },
    enabled: !!id,
    // CSR-2100: the carrier-wait statuses are the ones that change without
    // anyone in this tab doing anything — logrock-coi flips them when the
    // carrier answers. Without polling, an inspector left open shows the wait
    // forever and eventually calls a finished request stuck. Polling stops on
    // its own as soon as the refreshed status is anything else.
    refetchInterval: (query) =>
      isAwaitingCarrier(query.state.data?.status ?? "") ? 30_000 : false,
  });
}

// Intercom Events API hooks (CSR-798)
export type IntercomEventListItem = Omit<IntercomEvent, "eventData">;

export interface IntercomEventsQueryOptions {
  agencyId?: string | null;
  page?: number;
  pageSize?: number;
  q?: string;
  status?: IntercomStatusFilter;
  sort?: IntercomSortField;
  sortDir?: SortDir;
  enabled?: boolean;
}

export type IntercomEventsResponse = PaginatedResponse<IntercomEventListItem>;

export function useIntercomEvents(options: IntercomEventsQueryOptions = {}) {
  const { agencyId, page, pageSize, q, status, sort, sortDir, enabled } = options;
  return useQuery<IntercomEventsResponse>({
    queryKey: ["intercom-events", { agencyId, page, pageSize, q, status, sort, sortDir }],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (agencyId) params.set("agencyId", agencyId);
      if (page != null) params.set("page", String(page));
      if (pageSize != null) params.set("pageSize", String(pageSize));
      if (q) params.set("q", q);
      if (status) params.set("status", status);
      if (sort) params.set("sort", sort);
      if (sortDir) params.set("sortDir", sortDir);
      const url =
        params.size > 0
          ? `${API_BASE}/intercom-events?${params.toString()}`
          : `${API_BASE}/intercom-events`;
      const response = await apiFetch(url, { credentials: "include" });
      if (!response.ok) throw new Error("Failed to fetch Intercom events");
      return response.json();
    },
    placeholderData: keepPreviousData,
    enabled: enabled !== false,
  });
}

export type IntercomEventsStats = {
  received: { count: number };
  processing: { count: number };
  processed: { count: number };
  skipped: { count: number };
  failed: { count: number };
};

export function useIntercomEventsStats(agencyId?: string | null) {
  return useQuery<IntercomEventsStats>({
    queryKey: ["intercom-events-stats", agencyId],
    queryFn: async () => {
      const url = agencyId
        ? `${API_BASE}/intercom-events/stats?agencyId=${agencyId}`
        : `${API_BASE}/intercom-events/stats`;
      const response = await apiFetch(url, { credentials: "include" });
      if (!response.ok) throw new Error("Failed to fetch Intercom events stats");
      return response.json();
    },
  });
}

export function useIntercomEvent(id: string | null) {
  return useQuery<IntercomEvent>({
    queryKey: ["intercom-event", id],
    queryFn: async () => {
      const response = await apiFetch(`${API_BASE}/intercom-events/${id}`, { credentials: "include" });
      if (!response.ok) throw new Error("Failed to fetch Intercom event");
      return response.json();
    },
    enabled: !!id,
  });
}

// ============================================================
// COI Exclusion Phrases (admin CRUD — CSR-710)
// ============================================================
// Re-export the schema-derived types so callers can import everything
// from "@/lib/api" without reaching into "@shared/schema" directly.

// ============================================================
// Feature Flags (admin CRUD)
// ============================================================
export function useFeatureFlags(query?: string) {
  const trimmed = query?.trim() ?? "";
  return useQuery<FeatureFlag[]>({
    queryKey: ["feature-flags", trimmed],
    queryFn: async () => {
      const url = trimmed
        ? `${API_BASE}/feature-flags?q=${encodeURIComponent(trimmed)}`
        : `${API_BASE}/feature-flags`;
      const response = await apiFetch(url, { credentials: "include" });
      if (!response.ok) throw new Error("Failed to fetch feature flags");
      return response.json();
    },
    placeholderData: keepPreviousData,
  });
}

// CSR-1951 — boolean gate over useFeatureFlags(). Fetches the full flag list
// (the `?q=` search is a fuzzy match, so the exact-name check happens here) and
// returns whether a flag with this exact name exists AND is active. Loading or
// missing → false (closed by default, mirroring the server posture).
export function useFeatureFlag(name: string): boolean {
  const { data } = useFeatureFlags();
  return (data ?? []).some((flag) => flag.name === name && flag.isActive);
}

export function useCreateFeatureFlag() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: InsertFeatureFlag) => {
      const response = await apiFetch(`${API_BASE}/feature-flags`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
        credentials: "include",
      });
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.error || "Failed to create feature flag");
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["feature-flags"] });
    },
  });
}

export function useUpdateFeatureFlag() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: UpdateFeatureFlag }) => {
      const response = await apiFetch(`${API_BASE}/feature-flags/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
        credentials: "include",
      });
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.error || "Failed to update feature flag");
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["feature-flags"] });
    },
  });
}

export function useDeleteFeatureFlag() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const response = await apiFetch(`${API_BASE}/feature-flags/${id}`, {
        method: "DELETE",
        credentials: "include",
      });
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.error || "Failed to delete feature flag");
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["feature-flags"] });
    },
  });
}

// ─── Insurance Companies (CSR-588) ──────────────────────────────────────────

// CSR-822: server pre-buckets the list so the client doesn't have to sort
// rows by category. Counts are derivable on the client (child companies)
// or via per-id queries (policy count, see useInsuranceCompanyPolicyCount).
export type InsuranceCompaniesByCategory = {
  parentCompanies: InsuranceCompany[];
  insuranceCarriers: InsuranceCompany[];
  mgas: InsuranceCompany[];
};

export function useInsuranceCompanies() {
  return useQuery<InsuranceCompaniesByCategory>({
    queryKey: ["insurance-companies"],
    queryFn: async () => {
      const response = await apiFetch(`${API_BASE}/insurance-companies`, {
        credentials: "include",
      });
      if (!response.ok) throw new Error("Failed to fetch insurance companies");
      return response.json();
    },
  });
}

// CSR-824: single-row read for the detail page.
export function useInsuranceCompany(id: string | null | undefined) {
  return useQuery<InsuranceCompany>({
    queryKey: ["insurance-companies", id],
    queryFn: async () => {
      const response = await apiFetch(`${API_BASE}/insurance-companies/${id}`, {
        credentials: "include",
      });
      if (!response.ok) throw new Error("Failed to fetch insurance company");
      return response.json();
    },
    enabled: !!id,
  });
}

// CSR-824: paginated policies linked to a single insurance company.
// Uses the CSR-819 derivative endpoint; payload shape mirrors
// `/api/policies` (`{ data, total, page, pageSize }`).
export function useInsuranceCompanyPolicies(
  id: string | null | undefined,
  page: number,
  pageSize: number,
) {
  return useQuery<PaginatedResponse<Policy>>({
    queryKey: ["insurance-companies", id, "policies", page, pageSize],
    queryFn: async () => {
      const response = await apiFetch(
        `${API_BASE}/insurance-companies/${id}/policies?page=${page}&pageSize=${pageSize}`,
        { credentials: "include" },
      );
      if (!response.ok) throw new Error("Failed to fetch policies for company");
      return response.json();
    },
    enabled: !!id,
    placeholderData: keepPreviousData,
  });
}

// CSR-822: per-company policy count for the list-view cards. Uses the
// CSR-819 derivative endpoint with pageSize=1 -- the response carries the
// canonical `total` so we never have to page through actual rows. One
// fetch per visible card; acceptable for typical agency volumes. A future
// aggregate endpoint would collapse this into a single round-trip.
export function useInsuranceCompanyPolicyCount(id: string, enabled: boolean = true) {
  return useQuery<number>({
    queryKey: ["insurance-companies", id, "policies-count"],
    queryFn: async () => {
      const response = await apiFetch(
        `${API_BASE}/insurance-companies/${id}/policies?page=1&pageSize=1`,
        { credentials: "include" },
      );
      if (!response.ok) throw new Error("Failed to fetch policy count");
      const body = (await response.json()) as { total: number };
      return typeof body.total === "number" ? body.total : 0;
    },
    enabled: enabled && !!id,
    staleTime: 60_000,
  });
}

// CSR-856: children list for the Parent Company variant of the detail
// page. Backend route is /api/insurance-companies/:id/children; the
// payload is a lightweight projection (no commissions/address) with a
// pre-aggregated policy count per child.
export interface InsuranceCompanyChild {
  id: string;
  name: string;
  category: "parent_company" | "insurance_carrier" | "mga";
  naic: string | null;
  policyCount: number;
}

export function useInsuranceCompanyChildren(id: string | null | undefined) {
  return useQuery<InsuranceCompanyChild[]>({
    queryKey: ["insurance-companies", id, "children"],
    queryFn: async () => {
      const response = await apiFetch(
        `${API_BASE}/insurance-companies/${id}/children`,
        { credentials: "include" },
      );
      if (!response.ok) throw new Error("Failed to fetch children");
      return response.json();
    },
    enabled: !!id,
    staleTime: 60_000,
  });
}

// CSR-885 — Quick Stats source. Returns the active policy count for the
// company; on parent_company rows the backend aggregates across all
// alive descendants.
export interface InsuranceCompanyStats {
  activePolicyCount: number;
}

export function useInsuranceCompanyStats(id: string | null | undefined) {
  return useQuery<InsuranceCompanyStats>({
    queryKey: ["insurance-companies", id, "stats"],
    queryFn: async () => {
      const response = await apiFetch(
        `${API_BASE}/insurance-companies/${id}/stats`,
        { credentials: "include" },
      );
      if (!response.ok) throw new Error("Failed to fetch stats");
      return response.json();
    },
    enabled: !!id,
    staleTime: 60_000,
  });
}

// CSR-823: create / update mutations for Insurance Companies. POST omits
// the id field so the backend generates the canonical `INSCO-XXXXXXXX`.
// Both mutations invalidate the list query and (on update) the per-row
// caches the detail page (CSR-824/825) will rely on.
export type InsertInsuranceCompanyClient = Omit<InsertInsuranceCompany, "id">;

export function useCreateInsuranceCompany() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: InsertInsuranceCompanyClient) => {
      const response = await apiFetch(`${API_BASE}/insurance-companies`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
        credentials: "include",
      });
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.error || "Failed to create insurance company");
      }
      return (await response.json()) as InsuranceCompany;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["insurance-companies"] });
    },
  });
}

export function useUpdateInsuranceCompany() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (args: { id: string; data: UpdateInsuranceCompany }) => {
      const response = await apiFetch(
        `${API_BASE}/insurance-companies/${args.id}`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(args.data),
          credentials: "include",
        },
      );
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.error || "Failed to update insurance company");
      }
      return (await response.json()) as InsuranceCompany;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["insurance-companies"] });
    },
  });
}

// CSR-857: Insurance Company contacts hooks. Backend routes (CSR-820):
//   * GET  /api/insurance-companies/:id/contacts
//   * POST /api/insurance-companies/:id/contacts           (nested URL)
//   * PATCH  /api/insurance-company-contacts/:id           (flat URL)
//   * DELETE /api/insurance-company-contacts/:id           (flat URL)
// The id is server-generated as `INCT-XXXXXXXX` so client mutations omit it.
export type InsertInsuranceCompanyContactClient = Omit<
  InsertInsuranceCompanyContact,
  "id" | "insuranceCompanyId"
>;

export function useInsuranceCompanyContacts(id: string | null | undefined) {
  return useQuery<InsuranceCompanyContact[]>({
    queryKey: ["insurance-companies", id, "contacts"],
    queryFn: async () => {
      const response = await apiFetch(
        `${API_BASE}/insurance-companies/${id}/contacts`,
        { credentials: "include" },
      );
      if (!response.ok) throw new Error("Failed to fetch contacts");
      return response.json();
    },
    enabled: !!id,
    staleTime: 60_000,
  });
}

export function useCreateInsuranceCompanyContact() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (args: {
      companyId: string;
      data: InsertInsuranceCompanyContactClient;
    }) => {
      const response = await apiFetch(
        `${API_BASE}/insurance-companies/${args.companyId}/contacts`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(args.data),
          credentials: "include",
        },
      );
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.error || "Failed to create contact");
      }
      return (await response.json()) as InsuranceCompanyContact;
    },
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({
        queryKey: ["insurance-companies", vars.companyId, "contacts"],
      });
    },
  });
}

export function useUpdateInsuranceCompanyContact() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (args: {
      id: string;
      companyId: string;
      data: UpdateInsuranceCompanyContact;
    }) => {
      const response = await apiFetch(
        `${API_BASE}/insurance-company-contacts/${args.id}`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(args.data),
          credentials: "include",
        },
      );
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.error || "Failed to update contact");
      }
      return (await response.json()) as InsuranceCompanyContact;
    },
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({
        queryKey: ["insurance-companies", vars.companyId, "contacts"],
      });
    },
  });
}

// CSR-1606 — carrier commission overrides by line of business.

export function useInsuranceCompanyCommissionRates(id: string | null | undefined) {
  return useQuery<InsuranceCompanyCommissionRate[]>({
    queryKey: ["insurance-companies", id, "commission-rates"],
    queryFn: async () => {
      const response = await apiFetch(
        `${API_BASE}/insurance-companies/${id}/commission-rates`,
        { credentials: "include" },
      );
      if (!response.ok) throw new Error("Failed to fetch commission rates");
      return response.json();
    },
    enabled: !!id,
    staleTime: 60_000,
  });
}

export function useCreateInsuranceCompanyCommissionRate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (args: {
      companyId: string;
      data: { lineOfBusiness: string; ratePct: string };
    }) => {
      const response = await apiFetch(
        `${API_BASE}/insurance-companies/${args.companyId}/commission-rates`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(args.data),
          credentials: "include",
        },
      );
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.error || "Failed to create commission rate");
      }
      return (await response.json()) as InsuranceCompanyCommissionRate;
    },
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({
        queryKey: ["insurance-companies", vars.companyId, "commission-rates"],
      });
    },
  });
}

export function useDeleteInsuranceCompanyCommissionRate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (args: { id: string; companyId: string }) => {
      const response = await apiFetch(
        `${API_BASE}/insurance-companies/${args.companyId}/commission-rates/${args.id}`,
        { method: "DELETE", credentials: "include" },
      );
      if (!response.ok && response.status !== 204) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.error || "Failed to delete commission rate");
      }
    },
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({
        queryKey: ["insurance-companies", vars.companyId, "commission-rates"],
      });
    },
  });
}

// CSR-1606 — Line of Business options (canonical baseline ∪ types present in
// the synced data). Feeds the carrier override dropdown and the Add Policy
// "Type" field.
export function usePolicyTypes() {
  return useQuery<string[]>({
    queryKey: ["policies", "types"],
    queryFn: async () => {
      const response = await apiFetch(`${API_BASE}/policies/types`, {
        credentials: "include",
      });
      if (!response.ok) throw new Error("Failed to fetch policy types");
      return response.json();
    },
    staleTime: 5 * 60_000,
  });
}

export function useDeleteInsuranceCompanyContact() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (args: { id: string; companyId: string }) => {
      const response = await apiFetch(
        `${API_BASE}/insurance-company-contacts/${args.id}`,
        { method: "DELETE", credentials: "include" },
      );
      if (!response.ok && response.status !== 204) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.error || "Failed to delete contact");
      }
    },
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({
        queryKey: ["insurance-companies", vars.companyId, "contacts"],
      });
    },
  });
}
